/**
 * OP Online Reader — Test Route /online-reader-test
 * Isolated cockpit reader. Does NOT replace MagazinePage or the existing reader.
 * Source-of-truth: OP Reader Cockpit.html prototype
 */
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Link, useParams } from 'react-router-dom';
import { Headphones } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
// Use latest issue — no hardcoded slug so the reader always loads the current issue
const ISSUE_SLUG = null;

// ─── Reader Color Tokens (scoped, not global) ─────────────────────────────────
// Registered here and documented; do NOT bleed into global --op-* tokens.
const R = {
  outer:       '#F5F3EE',
  rail:        '#FFFFFF',
  railBorder:  '#E5E1D8',
  railMuted:   '#F7F4EE',
  railActive:  '#081D30',
  canvas:      '#FFFDF8',
  canvasBar:   '#FFFDF8',
  divider:     '#EDE9DF',
  segDot:      '#D8D3C8',
  progress:    '#C49630',
  tealBadge:   'rgba(47,148,134,0.12)',
  tealBadgeText: '#1D6B62',
  tealText:    '#2F9486',
  tealDeep:    '#2F9486',
  navyText:    '#081D30',
  bodyText:    '#2C3E46',
  deikText:    '#4F6066',
  metaText:    '#6A7B80',
  mutedText:   '#94A3B0',
  btnBorder:   '#D8D3C8',
  btnText:     '#1A2E3B',
  nextBtnBg:   '#081D30',
  nextBtnText: '#FFFDF8',
  railText:    '#1A2E3B',
  railSub:     '#6B7F8C',
  railAccent:  '#2F9486',
  railDivider: '#EDE9DF',
  goldAccent:  '#C49630',
  gateOverlay: 'linear-gradient(to bottom, rgba(255,253,248,0) 0%, rgba(255,253,248,0.92) 40%, rgba(255,253,248,1) 100%)',
};

// ─── Font loader ──────────────────────────────────────────────────────────────
function useReaderFonts() {
  useEffect(() => {
    const id = 'op-reader-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&family=Archivo:wght@400;500;600;700;800&family=Source+Serif+4:ital,wght@0,400;0,600;1,400;1,600&display=swap';
    document.head.appendChild(link);
  }, []);
}

// ─── Body Segmenter ───────────────────────────────────────────────────────────
const segmentBody = (html = '') => {
  const h2Splits = html.split(/<h2[^>]*>/i);
  if (h2Splits.length > 2) {
    return h2Splits.map((part, i) => (i === 0 ? part : `<h2>${part}`)).filter(s => s.trim());
  }
  const paras = html.match(/<p[\s\S]*?<\/p>/gi) || [];
  if (paras.length <= 3) return [html];
  const chunk = Math.ceil(paras.length / 3);
  return [
    paras.slice(0, chunk).join(''),
    paras.slice(chunk, chunk * 2).join(''),
    paras.slice(chunk * 2).join(''),
  ].filter(Boolean);
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const capitalize = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const readTime = body => {
  if (!body) return '4 min read';
  const words = body.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 220))} min read`;
};
const escTxt = (s) => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

// ─── Pre-measurement: global article page count ───────────────────────────────
// Identical packing algorithm to runPaginate — returns an integer page count
// without touching React state. Used only for building the global issue sequence.
function measureArticlePageCount(article, frameH, frameW, fontSize, hiddenDiv) {
  if (!article?.body || !hiddenDiv || frameH < 60 || frameW < 60) return 1;
  const div = document.createElement('div');
  div.innerHTML = DOMPurify.sanitize(article.body);
  const bodyBlocks = Array.from(div.children).map(el => el.outerHTML).filter(Boolean);
  if (!bodyBlocks.length) return 1;

  const CONTENT_W = Math.min(716, frameW - 88);
  const PAD_TOP = 24;
  const SAFETY = 10;

  hiddenDiv.style.cssText = [
    'position:fixed', 'top:-9999px', 'left:0', 'visibility:hidden',
    'pointer-events:none', 'z-index:-1',
    `width:${CONTENT_W}px`,
    "font-family:'Source Serif 4',serif",
    `font-size:${fontSize}px`,
    'line-height:1.75',
  ].join(';') + ';';

  const hasImg = !!(article.hero_image_url || article.featured_image_url);
  const imgH = hasImg ? CONTENT_W * (9 / 16) + 22 : 0;

  hiddenDiv.innerHTML = `<h1 style="margin:0 0 12px;font-family:'Source Serif 4',serif;font-size:34px;line-height:1.12;font-weight:600;word-wrap:break-word;">${escTxt(article.title)}</h1>`;
  const titleH = hiddenDiv.scrollHeight + 12;

  let dekH = 0;
  if (article.dek) {
    hiddenDiv.innerHTML = `<p style="margin:0 0 18px;font-family:'Source Serif 4',serif;font-size:18px;line-height:1.5;font-style:italic;">${escTxt(article.dek)}</p>`;
    dekH = hiddenDiv.scrollHeight + 18;
  }
  const headerH = imgH + titleH + dekH + 56 + 24; // 56 = metaH, 24 = bodyGap
  const BUDGET_0 = Math.max(60, frameH - PAD_TOP - headerH - SAFETY);
  const BUDGET_N = Math.max(60, frameH - PAD_TOP * 2 - SAFETY);

  const blockH = bodyBlocks.map(html => {
    hiddenDiv.innerHTML = `<div style="font-family:'Source Serif 4',serif;font-size:${fontSize}px;line-height:1.75;">${html}</div>`;
    return Math.max(1, hiddenDiv.scrollHeight);
  });

  let pageCount = 0, bIdx = 0, isFirst = true;
  while (bIdx < bodyBlocks.length) {
    const budget = isFirst ? BUDGET_0 : BUDGET_N;
    const pg = []; let used = 0;
    while (bIdx < bodyBlocks.length) {
      const h = blockH[bIdx], html = bodyBlocks[bIdx];
      const isHdg = /^<h[1-6][\s>]/i.test(html.trim());
      const nextH = bIdx + 1 < blockH.length ? blockH[bIdx + 1] : 0;
      const needed = isHdg ? h + nextH : h;
      if (pg.length > 0 && used + needed > budget) break;
      pg.push(html); used += h; bIdx++;
    }
    if (pg.length === 0 && bIdx < bodyBlocks.length) bIdx++;
    pageCount++; isFirst = false;
  }
  return Math.max(1, pageCount);
}

// ─── Top Exit Bar ─────────────────────────────────────────────────────────────
function ExitBar({ issue }) {
  return (
    <div
      data-testid="reader-exit-bar"
      style={{
        flex: '0 0 auto', background: R.rail, borderBottom: `1px solid ${R.railBorder}`,
        padding: '9px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        zIndex: 10,
      }}
    >
      <Link
        to="/"
        data-testid="reader-back-link"
        style={{
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, letterSpacing: 0.4,
          color: R.railAccent, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        ← Back to OpportunityPost.com
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 700, color: R.railText, letterSpacing: 0.2 }}>
          OP Monthly
        </span>
        <span style={{
          fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, letterSpacing: 0.8,
          color: R.tealBadgeText, background: R.tealBadge, padding: '3px 8px', borderRadius: 4,
          textTransform: 'uppercase',
        }}>
          Jan/Feb 2026
        </span>
        <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.railSub }}>
          Test Route
        </span>
      </div>
    </div>
  );
}

// ─── Left Rail ────────────────────────────────────────────────────────────────
function LeftRail({ articles, selectedIdx, onSelect, issue, scrollRef, sequenceMap }) {
  const editorialCount = articles.filter(a => !a._isAd && !a._isCover).length;

  return (
    <aside
      data-testid="reader-left-rail"
      style={{
        width: 288, minWidth: 288, background: '#FFFFFF',
        borderRight: '1px solid #E5E1D8',
        display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
      }}
    >
      {/* Masthead strip — fixed */}
      <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid #EDE9DF', flexShrink: 0 }}>
        <div style={{ fontFamily: 'Arquivo, Archivo, sans-serif', fontSize: 9.5, fontWeight: 800, letterSpacing: 2.2, color: '#6B7F8C', textTransform: 'uppercase' }}>
          Opportunity Post
        </div>
        <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 8, letterSpacing: 1.2, color: '#94A3B0', textTransform: 'uppercase', marginTop: 1 }}>
          Magazine
        </div>
      </div>

      {/* Cover image — fixed, never scrolls */}
      <div style={{ flexShrink: 0, borderBottom: '1px solid #EDE9DF' }}>
        <div style={{ width: '100%', aspectRatio: '3/4', overflow: 'hidden', position: 'relative', background: '#081D30' }}>
          {issue?.cover_image_url
            ? <img src={issue.cover_image_url} alt="Cover" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'linear-gradient(160deg, #081D30 0%, #0f3040 55%, #1E4A5C 100%)' }}>
                <div style={{ fontFamily: 'Source Serif 4, serif', fontSize: 36, color: '#FFFDF8', lineHeight: 1, fontWeight: 400 }}>OP</div>
                <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 8, color: 'rgba(255,255,255,0.4)', letterSpacing: 1.8, textTransform: 'uppercase' }}>
                  {issue?.issue_date || 'Monthly'}
                </div>
              </div>
          }
        </div>
      </div>

      {/* Issue info — fixed */}
      <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid #EDE9DF', flexShrink: 0 }}>
        <div style={{ fontFamily: 'Newsreader, Georgia, serif', fontSize: 15, fontWeight: 500, color: '#081D30', marginBottom: 3, lineHeight: 1.2 }}>
          {issue?.issue_date || 'Monthly Edition'}
        </div>
        <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 10, color: '#6B7F8C', display: 'flex', gap: 8 }}>
          {issue?.volume_number && <span>Vol. {issue.volume_number}</span>}
          <span>{editorialCount} {editorialCount === 1 ? 'story' : 'stories'}</span>
        </div>
      </div>

      {/* TOC — scrolls independently beneath the fixed cover */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {/* TOC heading */}
        <div style={{ padding: '10px 16px 6px', flexShrink: 0 }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#94A3B0' }}>
            Table of Contents
          </span>
        </div>

        {/* TOC items — editorial articles only, no ads */}
        {articles.map((art, idx) => {
          if (art._isAd || art._isCover) return null;
          const active = idx === selectedIdx;
          const thumb = art.featured_image_url || art.hero_image_url;
          const category = capitalize(art.primary_pillar || art.pillar || 'Editorial');
          return (
            <button
              key={art.id}
              data-testid={`reader-toc-item-${idx}`}
              onClick={() => onSelect(idx)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                padding: '8px 12px 8px 10px', border: 'none', cursor: 'pointer', flexShrink: 0,
                background: active ? '#F7F3EC' : 'transparent',
                borderLeft: `3px solid ${active ? '#081D30' : 'transparent'}`,
                transition: 'background 0.15s ease',
              }}
            >
              {/* Thumbnail */}
              <div style={{ width: 44, height: 44, borderRadius: 5, overflow: 'hidden', flexShrink: 0, background: '#EDE9DF' }}>
                {thumb
                  ? <img src={thumb} alt={art.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #EDE9DF 0%, #D8D3C8 100%)' }} />
                }
              </div>

              {/* Text */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: 'Archivo, sans-serif', fontSize: 14, fontWeight: active ? 700 : 500,
                  color: '#081D30', lineHeight: 1.35,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {art.title}
                </div>
                <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 11, color: active ? '#6B4A1A' : '#6B7F8C', marginTop: 2 }}>
                  {category}
                </div>
              </div>

              {/* Global page number */}
              {(sequenceMap?.[art.id]?.globalStartPage || art.pageNum) ? (
                <span style={{
                  fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: active ? 700 : 500,
                  color: active ? '#081D30' : '#94A3B0', flexShrink: 0, marginLeft: 2,
                }}>
                  p.{sequenceMap?.[art.id]?.globalStartPage ?? art.pageNum}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </aside>
  );
}
// ─── Canvas Top Bar ────────────────────────────────────────────────────────────
// ─── Animated equalizer bars — shown when audio is playing ───────────────────
function EqBars() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, height: 14, flexShrink: 0 }}>
      <span className="op-eq-bar op-eq-bar-1" />
      <span className="op-eq-bar op-eq-bar-2" />
      <span className="op-eq-bar op-eq-bar-3" />
    </span>
  );
}

// LEFT: per-article progress dots (one per body page, clickable, no dots for ads).
// CENTER: article title — mathematically centered via absolute positioning.
// RIGHT: headphones icon | gap+equalizer bars | A−/A+ controls.
function CanvasTopBar({ article, segIdx, setSegIdx, pages, fontSize, setFontSize, onToggleAudio, audioPlaying }) {
  const isEndPage = segIdx >= pages.length;
  return (
    <div
      data-testid="reader-topbar"
      style={{
        flex: '0 0 auto', background: '#FFFDF8', borderBottom: '1px solid #EDE9DF',
        padding: '7px 14px', display: 'flex', alignItems: 'center',
        minHeight: 44, position: 'relative', gap: 0,
      }}
    >
      {/* LEFT: article-progress dots */}
      <div
        data-testid="reader-progress-dots"
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          flexShrink: 1, overflow: 'hidden', flexWrap: 'nowrap',
        }}
      >
        {pages.map((_, i) => {
          const isActive = !isEndPage && i === segIdx;
          const isRead   = isEndPage || i < segIdx;
          return (
            <button
              key={i}
              data-testid={`reader-dot-${i}`}
              onClick={() => setSegIdx(i)}
              title={`Page ${i + 1}`}
              style={{
                width: isActive ? 20 : 7, height: 7, borderRadius: 4,
                background: (isActive || isRead) ? R.progress : R.segDot,
                border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0,
                transition: 'width 0.2s ease, background 0.15s ease',
              }}
            />
          );
        })}
      </div>

      {/* CENTER: article title — absolutely centered so side widths don't affect it */}
      <div
        style={{
          position: 'absolute', left: '50%', transform: 'translateX(-50%)',
          maxWidth: '52%', overflow: 'hidden', pointerEvents: 'none',
        }}
      >
        <span
          data-testid="reader-topbar-title"
          style={{
            display: 'block',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontFamily: 'Archivo, sans-serif', fontSize: 12.5, fontWeight: 600,
            lineHeight: 1.35, letterSpacing: -0.1, color: R.navyText,
            textAlign: 'center',
          }}
        >
          {article?.title || ''}
        </span>
      </div>

      {/* RIGHT: headphones | equalizer gap | A−/A+ controls */}
      <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', flexShrink: 0 }}>
        {/* Headphones icon — turns green when active */}
        <button
          data-testid="reader-headphones-btn"
          title={article?.audio_url ? (audioPlaying ? 'Pause audio' : 'Listen to article') : 'Listen'}
          onClick={article?.audio_url ? onToggleAudio : undefined}
          style={{
            width: 28, height: 26, borderRadius: 5,
            border: `1px solid ${audioPlaying ? '#22c55e' : R.btnBorder}`,
            background: audioPlaying ? '#f0fdf4' : '#fff',
            color: audioPlaying ? '#22c55e' : R.mutedText,
            cursor: article?.audio_url ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
            <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/>
            <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
          </svg>
        </button>
        {/* Gap — equalizer bars pulse here when playing */}
        <div style={{ width: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {audioPlaying && <EqBars />}
        </div>
        {/* Font size controls */}
        <button
          data-testid="reader-font-decrease"
          onClick={() => setFontSize(f => Math.max(13, f - 1))}
          style={{ width: 28, height: 26, borderRadius: 5, border: `1px solid ${R.btnBorder}`, background: '#fff', color: R.btnText, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
        >A−</button>
        <button
          data-testid="reader-font-increase"
          onClick={() => setFontSize(f => Math.min(22, f + 1))}
          style={{ width: 28, height: 26, borderRadius: 5, border: `1px solid ${R.btnBorder}`, background: '#fff', color: R.btnText, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginLeft: 4 }}
        >A+</button>
      </div>
    </div>
  );
}

// ─── Reading Canvas Bottom Bar ────────────────────────────────────────────────
// Simplified: Contents (left) | "Page X of Y" global counter (center) | Back + Next (right)
function CanvasBottomBar({ onPrev, onNext, canGoBack, canGoNext, globalCurrentPage, totalGlobalPages, onContents }) {
  return (
    <div style={{
      flex: '0 0 auto', background: '#FFFDF8', borderTop: '1px solid #EDE9DF',
      padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 12,
    }}>
      {/* Left: Contents */}
      <button
        data-testid="reader-contents-btn"
        onClick={onContents}
        style={{
          fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 600,
          padding: '7px 14px', borderRadius: 6, border: '1px solid #D8D3C8',
          background: '#fff', color: '#081D30', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
        }}
      >
        ≡ Contents
      </button>

      {/* Center: global page counter — single source of truth */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span
          data-testid="reader-global-page-counter"
          style={{
            fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
            color: '#6B7F8C', letterSpacing: 0.3, whiteSpace: 'nowrap',
          }}
        >
          {globalCurrentPage != null && totalGlobalPages != null
            ? `Page ${globalCurrentPage} of ${totalGlobalPages}`
            : globalCurrentPage != null ? `Page ${globalCurrentPage}` : ''}
        </span>
      </div>

      {/* Right: Back + Next */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        <button
          data-testid="reader-prev-btn"
          onClick={onPrev}
          disabled={!canGoBack}
          style={{
            fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 600,
            padding: '7px 16px', borderRadius: 6, border: '1px solid #D8D3C8',
            background: '#fff', color: canGoBack ? '#081D30' : '#94A3B0',
            cursor: canGoBack ? 'pointer' : 'default', opacity: canGoBack ? 1 : 0.38,
          }}
        >
          ← Back
        </button>
        <button
          data-testid="reader-next-btn"
          onClick={onNext}
          disabled={!canGoNext}
          style={{
            fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 600,
            padding: '7px 18px', borderRadius: 6,
            border: canGoNext ? '1px solid #081D30' : '1px solid #D8D3C8',
            background: canGoNext ? '#081D30' : '#fff',
            color: canGoNext ? '#FFFDF8' : '#94A3B0',
            cursor: canGoNext ? 'pointer' : 'default', opacity: canGoNext ? 1 : 0.38,
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

// ─── Gate Overlay ─────────────────────────────────────────────────────────────
function GateOverlay({ onScrollRight, reason }) {
  const isMemberGate = reason === 'member_archive';
  return (
    <div
      data-testid="reader-gate-overlay"
      style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: '65%',
        background: R.gateOverlay,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end',
        paddingBottom: 40, zIndex: 10,
      }}
    >
      <div style={{
        maxWidth: 380, width: '90%', textAlign: 'center',
        background: R.canvasBar, border: `1px solid ${R.divider}`,
        borderRadius: 12, padding: '24px 28px', boxShadow: '0 8px 32px rgba(10,35,46,0.12)',
      }}>
        <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 18, fontWeight: 800, color: R.navyText, marginBottom: 8, letterSpacing: -0.3 }}>
          {isMemberGate ? 'Member Archive' : 'Preview Complete'}
        </div>
        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: R.metaText, lineHeight: 1.6, marginBottom: 16 }}>
          {isMemberGate
            ? 'This issue is in the member archive. Get free reading access to continue.'
            : "You've used today's preview time. Get free access to continue reading."}
        </div>
        <button
          data-testid="reader-gate-cta"
          onClick={onScrollRight}
          style={{
            width: '100%', padding: '11px 0', borderRadius: 7, border: 'none',
            background: R.tealDeep, color: '#fff',
            fontFamily: 'Archivo, sans-serif', fontSize: 13, fontWeight: 700,
            cursor: 'pointer', letterSpacing: 0.3, marginBottom: 10,
          }}
        >
          Get Free Access →
        </button>
        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.mutedText }}>
          Enter your email in the panel on the right.
        </div>
      </div>
    </div>
  );
}

// ─── Cover Page Canvas ────────────────────────────────────────────────────────
function CoverPageCanvas({ article, hasNextArticle, onNextArticle }) {
  return (
    <main
      data-testid="reader-cover-canvas"
      style={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: '#0d1a24', overflow: 'hidden', position: 'relative',
      }}
    >
      {article.cover_image_url ? (
        <img
          src={article.cover_image_url}
          alt="Magazine Cover"
          style={{ maxHeight: '92%', maxWidth: '100%', objectFit: 'contain', display: 'block', borderRadius: 3 }}
        />
      ) : (
        <div style={{
          width: '56%', maxWidth: 340, aspectRatio: '2/3',
          background: '#1c2d3c', borderRadius: 6,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
        }}>
          <span style={{ fontFamily: 'Newsreader, Georgia, serif', fontSize: 16, color: '#FFFDF8', textAlign: 'center', padding: '0 20px' }}>
            {article.title}
          </span>
        </div>
      )}
      {hasNextArticle && (
        <button
          data-testid="reader-cover-start-btn"
          onClick={onNextArticle}
          style={{
            position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)',
            padding: '10px 28px', borderRadius: 7,
            background: '#FFFDF8', color: '#081D30', border: 'none',
            fontFamily: 'Archivo, sans-serif', fontSize: 13, fontWeight: 700,
            cursor: 'pointer', letterSpacing: 0.3, whiteSpace: 'nowrap',
            boxShadow: '0 2px 16px rgba(0,0,0,0.55)',
          }}
        >
          Start Reading →
        </button>
      )}
    </main>
  );
}

// ─── End of Article Panel ─────────────────────────────────────────────────────
function EndOfArticle({ article, hasNextArticle, onNextArticle }) {
  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '40px 44px', textAlign: 'center',
    }}>
      <div style={{
        width: 52, height: 52, borderRadius: '50%',
        background: 'rgba(47,148,134,0.10)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 18,
      }}>
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path d="M4 12l5 5L18 6" stroke="#2F9486" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div style={{
        fontFamily: 'IBM Plex Mono, monospace', fontSize: 9.5, letterSpacing: 1.4,
        textTransform: 'uppercase', color: R.metaText, marginBottom: 14,
      }}>
        End of Article
      </div>
      <div style={{
        fontFamily: 'Source Serif 4, serif', fontSize: 20, fontWeight: 600,
        color: R.navyText, lineHeight: 1.2, maxWidth: 380, marginBottom: 6,
      }}>
        {article.title}
      </div>
      {hasNextArticle && (
        <button
          data-testid="reader-end-next-btn"
          onClick={onNextArticle}
          style={{
            marginTop: 24,
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '10px 22px', borderRadius: 7,
            background: '#091c28', color: '#FFFDF8', border: 'none',
            fontFamily: 'Archivo, sans-serif', fontSize: 13, fontWeight: 700,
            cursor: 'pointer', letterSpacing: 0.3,
          }}
        >
          Continue to Next Article →
        </button>
      )}
    </div>
  );
}

// ─── Center Canvas ────────────────────────────────────────────────────────────
function CenterCanvas({
  article, segIdx, setSegIdx, fontSize, setFontSize,
  isGated, selectedIdx, articlesCount, onPrevArticle, onNextArticle,
  rightRailRef, onContents,
  onBodyFrameSized, sequenceMap, totalGlobalPages,
}) {
  // ── Slide-transition state ────────────────────────────────────────────────
  const [slideDir,   setSlideDir]  = useState(null);
  const [contentKey, setContentKey] = useState(0);
  const prevStateRef = useRef({ idx: selectedIdx, seg: segIdx });

  // ── Audio state ───────────────────────────────────────────────────────────
  const desktopAudioRef = useRef(null);
  const [desktopAudioPlaying, setDesktopAudioPlaying] = useState(false);

  // Pause + reset when article changes
  useEffect(() => {
    if (desktopAudioRef.current) {
      desktopAudioRef.current.pause();
      desktopAudioRef.current.currentTime = 0;
      setDesktopAudioPlaying(false);
    }
  }, [article?.id]); // eslint-disable-line

  const toggleDesktopAudio = () => {
    const el = desktopAudioRef.current;
    if (!el) return;
    if (desktopAudioPlaying) { el.pause(); } else { el.play(); }
  };

  useEffect(() => {
    const prev = prevStateRef.current;
    let dir = null;
    if (selectedIdx !== prev.idx) dir = selectedIdx > prev.idx ? 'next' : 'prev';
    else if (segIdx !== prev.seg) dir = segIdx > prev.seg ? 'next' : 'prev';
    if (dir) { setSlideDir(dir); setContentKey(k => k + 1); }
    prevStateRef.current = { idx: selectedIdx, seg: segIdx };
  }, [selectedIdx, segIdx]); // eslint-disable-line

  // Reset to page 0 when article changes
  useEffect(() => { setSegIdx(0); }, [article?.id]); // eslint-disable-line

  // ── DOM-measured pagination ───────────────────────────────────────────────
  const bodyFrameRef = useRef(null);   // fixed-height reading area
  const hiddenRef    = useRef(null);   // off-screen measurement sandbox
  const [pages, setPages] = useState([]); // array of HTML strings per body page

  // Parse sanitised body HTML into block-level elements once per article
  const bodyBlocks = useMemo(() => {
    if (!article?.body) return [];
    const div = document.createElement('div');
    div.innerHTML = DOMPurify.sanitize(article.body);
    return Array.from(div.children).map(el => el.outerHTML).filter(Boolean);
  }, [article?.id, article?.body]); // eslint-disable-line

  const runPaginate = useCallback(() => {
    const frame = bodyFrameRef.current;
    const mDiv  = hiddenRef.current;
    if (!frame || !mDiv) return;

    const frameH = frame.offsetHeight;
    const frameW = frame.offsetWidth;
    if (frameH < 60 || frameW < 60) return;

    const CONTENT_W = Math.min(716, frameW - 88); // 44 px each side
    const PAD_TOP   = 24;
    const SAFETY    = 10;

    // Configure measurement sandbox to match rendered typography
    mDiv.style.width      = CONTENT_W + 'px';
    mDiv.style.fontFamily = "'Source Serif 4', serif";
    mDiv.style.fontSize   = fontSize + 'px';
    mDiv.style.lineHeight = '1.75';
    mDiv.style.position   = 'fixed';
    mDiv.style.top        = '-9999px';
    mDiv.style.left       = '0';
    mDiv.style.visibility = 'hidden';

    // ── Measure opening-page header height ──────────────────────────────
    const hasImg = !!(article?.hero_image_url || article?.featured_image_url);
    const imgH   = hasImg ? CONTENT_W * (9 / 16) + 22 : 0;

    mDiv.innerHTML = `<h1 style="margin:0 0 12px;font-family:'Source Serif 4',serif;font-size:34px;line-height:1.12;font-weight:600;word-wrap:break-word;">${escTxt(article?.title)}</h1>`;
    const titleH = mDiv.scrollHeight + 12;

    let dekH = 0;
    if (article?.dek) {
      mDiv.innerHTML = `<p style="margin:0 0 18px;font-family:'Source Serif 4',serif;font-size:18px;line-height:1.5;font-style:italic;">${escTxt(article.dek)}</p>`;
      dekH = mDiv.scrollHeight + 18;
    }
    const metaH   = 56; // byline row + border-bottom
    const bodyGap = 24; // marginTop before body
    const headerH = imgH + titleH + dekH + metaH + bodyGap;

    const BUDGET_0 = Math.max(60, frameH - PAD_TOP - headerH - SAFETY);
    const BUDGET_N = Math.max(60, frameH - PAD_TOP * 2 - SAFETY);

    if (!bodyBlocks.length) { setPages([]); return; }

    // ── Measure each block height ────────────────────────────────────────
    const blockH = bodyBlocks.map(html => {
      mDiv.innerHTML = `<div style="font-family:'Source Serif 4',serif;font-size:${fontSize}px;line-height:1.75;">${html}</div>`;
      return Math.max(1, mDiv.scrollHeight);
    });

    // ── Pack blocks into pages ───────────────────────────────────────────
    const newPages = [];
    let bIdx    = 0;
    let isFirst = true;

    while (bIdx < bodyBlocks.length) {
      const budget = isFirst ? BUDGET_0 : BUDGET_N;
      const pg     = [];
      let used     = 0;

      while (bIdx < bodyBlocks.length) {
        const h    = blockH[bIdx];
        const html = bodyBlocks[bIdx];
        // Widow prevention: keep a heading with the block that follows it
        const isHdg  = /^<h[1-6][\s>]/i.test(html.trim());
        const nextH  = bIdx + 1 < blockH.length ? blockH[bIdx + 1] : 0;
        const needed = isHdg ? h + nextH : h;

        if (pg.length > 0 && used + needed > budget) break;
        pg.push(html);
        used += h;
        bIdx++;
      }

      // Safety: if a single block is taller than the budget, force it onto the page
      if (pg.length === 0 && bIdx < bodyBlocks.length) {
        pg.push(bodyBlocks[bIdx]);
        bIdx++;
      }

      newPages.push(pg.join(''));
      isFirst = false;
    }

    setPages(newPages.length ? newPages : ['']);
  }, [bodyBlocks, fontSize, article?.id, article?.title, article?.dek, article?.hero_image_url, article?.featured_image_url]); // eslint-disable-line

  // Run immediately after layout is committed (before browser paint)
  useLayoutEffect(() => {
    runPaginate();
  }, [runPaginate]);

  // Re-measure on container resize (font-size change, window resize)
  useEffect(() => {
    const el = bodyFrameRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const frame = bodyFrameRef.current;
      if (frame && frame.offsetHeight > 0) {
        onBodyFrameSized?.({ h: frame.offsetHeight, w: frame.offsetWidth });
      }
      runPaginate();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [runPaginate, onBodyFrameSized]);

  // Re-measure after web fonts settle (Source Serif 4 affects line heights)
  useEffect(() => {
    document.fonts.ready.then(runPaginate);
  }, [runPaginate]);

  // Clamp segIdx if page count shrinks (e.g. on font increase)
  useEffect(() => {
    const max = pages.length; // valid range: 0..pages.length (end page)
    if (segIdx > max) setSegIdx(max);
  }, [pages.length]); // eslint-disable-line

  // ── Navigation ────────────────────────────────────────────────────────────
  const totalParts  = pages.length + 1; // body pages + end-of-article page
  const isEndPage   = segIdx >= pages.length;
  const currentHtml = !isEndPage ? (pages[segIdx] || '') : '';
  const progressPct = totalParts > 1 ? Math.round(((segIdx + 1) / totalParts) * 100) : 20;

  // Global page numbers: use sequenceMap when available, fall back to article index
  const currentSeq       = sequenceMap?.[article?.id];
  const globalStartPage  = currentSeq?.globalStartPage ?? (selectedIdx + 1);
  const globalCurrentPage = isEndPage
    ? globalStartPage + Math.max(0, pages.length - 1)
    : globalStartPage + segIdx;
  const effectiveTotalGlobalPages = totalGlobalPages ?? articlesCount;

  // Bottom-bar navigation enabled states
  const canGoBack = segIdx > 0 || selectedIdx > 0;
  const canGoNext = !isEndPage || selectedIdx < articlesCount - 1;

  const handleNext = () => {
    if (!isEndPage) setSegIdx(s => s + 1);
    else if (selectedIdx < articlesCount - 1) onNextArticle();
  };
  const handlePrev = () => {
    if (segIdx > 0) setSegIdx(s => s - 1);
    else if (selectedIdx > 0) onPrevArticle();
  };

  const scrollToRight = () => {
    if (rightRailRef?.current) rightRailRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  // ── Early returns ─────────────────────────────────────────────────────────
  if (!article) {
    return (
      <main data-testid="reader-center-canvas" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: R.canvas, alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, color: R.mutedText }}>Select an article to start reading</span>
      </main>
    );
  }
  if (article._isAd) {
    const adGlobalPage = sequenceMap?.[article.id]?.globalStartPage ?? (selectedIdx + 1);
    return (
      <AdPageCanvas
        article={article}
        selectedIdx={selectedIdx}
        articlesCount={articlesCount}
        globalCurrentPage={adGlobalPage}
        totalGlobalPages={effectiveTotalGlobalPages}
        onPrevArticle={onPrevArticle}
        onNextArticle={onNextArticle}
      />
    );
  }
  if (article._isCover) {
    return (
      <CoverPageCanvas
        article={article}
        hasNextArticle={selectedIdx < articlesCount - 1}
        onNextArticle={onNextArticle}
      />
    );
  }

  return (
    <main
      data-testid="reader-center-canvas"
      style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: R.canvas }}
    >
      {/* Off-screen measurement sandbox — must stay in DOM */}
      <div ref={hiddenRef} aria-hidden="true" style={{ position: 'fixed', top: '-9999px', left: 0, visibility: 'hidden', pointerEvents: 'none', zIndex: -1 }} />

      <CanvasTopBar article={article} segIdx={segIdx} setSegIdx={setSegIdx} pages={pages} fontSize={fontSize} setFontSize={setFontSize} onToggleAudio={toggleDesktopAudio} audioPlaying={desktopAudioPlaying} />

      {/* Hidden HTML5 audio element for desktop reader */}
      {article?.audio_url && (
        <audio
          ref={desktopAudioRef}
          src={article.audio_url}
          onPlay={() => setDesktopAudioPlaying(true)}
          onPause={() => setDesktopAudioPlaying(false)}
          onEnded={() => setDesktopAudioPlaying(false)}
          style={{ display: 'none' }}
        />
      )}

      {/* Reading progress bar */}
      <div style={{ flex: '0 0 auto', height: 3, background: R.divider }}>
        <div style={{ height: '100%', width: `${progressPct}%`, background: R.progress, transition: 'width 0.3s ease' }} />
      </div>

      {/* Fixed-height page frame — no vertical scroll */}
      <div ref={bodyFrameRef} style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <style>{`
          @keyframes _rd-enter-right{from{transform:translateX(52px);opacity:0}to{transform:translateX(0);opacity:1}}
          @keyframes _rd-enter-left {from{transform:translateX(-52px);opacity:0}to{transform:translateX(0);opacity:1}}
          .op-reader-body h2{font-family:Archivo,sans-serif;font-size:21px;font-weight:700;letter-spacing:-0.3px;color:${R.navyText};margin:24px 0 14px}
          .op-reader-body p{margin:0 0 1.15em}
          .op-reader-body a{color:${R.tealText}}
          .op-reader-body ul,.op-reader-body ol{margin:0 0 1.15em;padding-left:1.5em}
          .op-reader-body li{margin-bottom:0.4em}
          .op-reader-body blockquote{margin:1em 0;padding:12px 20px;border-left:3px solid ${R.tealText};font-style:italic;color:${R.metaText}}
        `}</style>

        <article
          key={contentKey}
          style={{
            height: '100%',
            overflow: 'hidden',
            maxWidth: 760,
            margin: '0 auto',
            padding: '24px 44px',
            boxSizing: 'border-box',
            animation: slideDir === 'next' ? '_rd-enter-right 260ms cubic-bezier(0,0,.2,1) both'
                     : slideDir === 'prev' ? '_rd-enter-left  260ms cubic-bezier(0,0,.2,1) both'
                     : 'none',
          }}
        >
          {isEndPage ? (
            <EndOfArticle
              article={article}
              hasNextArticle={selectedIdx < articlesCount - 1}
              onNextArticle={onNextArticle}
            />
          ) : (
            <>
              {/* ── Opening page: hero + header + first page body ── */}
              {segIdx === 0 && (
                <>
                  {/* Featured image */}
                  <div style={{
                    position: 'relative', width: '100%', aspectRatio: '16/9',
                    borderRadius: 9, overflow: 'hidden',
                    background: 'repeating-linear-gradient(45deg,#d4d1c1 0,#d4d1c1 15px,#dcd9ca 15px,#dcd9ca 30px)',
                    border: `1px solid ${R.divider}`, marginBottom: 22,
                  }}>
                    {(article.hero_image_url || article.featured_image_url) && (
                      <img
                        src={article.hero_image_url || article.featured_image_url}
                        alt={article.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <span style={{
                      position: 'absolute', bottom: 12, left: 12,
                      fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, letterSpacing: 1,
                      color: R.tealBadgeText, background: R.tealBadge,
                      padding: '5px 10px', borderRadius: 5, textTransform: 'uppercase',
                    }}>
                      {capitalize(article.primary_pillar || 'editorial')}
                    </span>
                  </div>

                  {/* Headline */}
                  <h1 data-testid="reader-article-title" style={{
                    margin: '0 0 12px', fontFamily: 'Source Serif 4, serif', fontSize: 34,
                    lineHeight: 1.12, fontWeight: 600, letterSpacing: -0.3, color: R.navyText,
                  }}>
                    {article.title}
                  </h1>

                  {/* Dek */}
                  {article.dek && (
                    <p style={{
                      margin: '0 0 18px', fontFamily: 'Source Serif 4, serif', fontSize: 18,
                      lineHeight: 1.5, fontStyle: 'italic', color: R.deikText,
                    }}>
                      {article.dek}
                    </p>
                  )}

                  {/* Byline / meta */}
                  <div data-testid="reader-article-meta" style={{
                    display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                    fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, letterSpacing: 0.4,
                    color: R.metaText, paddingBottom: 22, borderBottom: `1px solid ${R.divider}`,
                  }}>
                    <span style={{ color: R.tealText, fontWeight: 600 }}>
                      {article.byline || 'By Opportunity Post Editorial Team'}
                    </span>
                    <span>{readTime(article.body)}</span>
                    {article.audio_url && (
                      <button
                        data-testid="reader-listen-btn"
                        onClick={toggleDesktopAudio}
                        style={{
                          marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '4px 12px', borderRadius: 6,
                          border: `1px solid ${desktopAudioPlaying ? '#C9A227' : 'rgba(8,29,48,0.20)'}`,
                          background: desktopAudioPlaying ? '#FFF8E6' : 'transparent',
                          cursor: 'pointer',
                          fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: 600,
                          color: desktopAudioPlaying ? '#C9A227' : '#081D30', letterSpacing: 0.2,
                        }}
                      >
                        {desktopAudioPlaying ? 'Pause' : 'Listen'}
                      </button>
                    )}
                  </div>
                </>
              )}

              {/* ── Page body (opening page: after header; continuation pages: standalone) ── */}
              <div
                data-testid={segIdx === 0 ? 'reader-article-body' : `reader-page-body-${segIdx}`}
                style={{
                  fontFamily: 'Source Serif 4, serif', fontSize, lineHeight: 1.75, color: R.bodyText,
                  marginTop: segIdx === 0 ? 24 : 0,
                  overflow: 'hidden',
                }}
              >
                <div className="op-reader-body" dangerouslySetInnerHTML={{ __html: currentHtml }} />
              </div>
            </>
          )}
        </article>
      </div>

      <CanvasBottomBar
        onPrev={handlePrev}
        onNext={handleNext}
        canGoBack={canGoBack}
        canGoNext={canGoNext}
        globalCurrentPage={globalCurrentPage}
        totalGlobalPages={effectiveTotalGlobalPages}
        onContents={onContents}
      />
    </main>
  );
}

// ─── Ad / Sponsor Page Canvas ─────────────────────────────────────────────────
// Renders an existing magazine sponsor/ad page (page_type: inside_front_sponsor,
// sponsor_full_page, inside_back_sponsor) using existing CMS fields only.
// Never gated — sponsor content is not subject to the OP Pass member unlock.
// Governance: page frame is always a true 842:1191 portrait magazine page —
// full-page ad by default, or split into two independently-clickable halves
// when ad_layout === 'two_half_page'. CTA/sponsor metadata renders in a compact
// side rail on desktop, never underneath the page frame.

// One image/CTA slot — used for the full-page image and for each half in two-half mode.
function AdImageSlot({ imageUrl, ctaUrl, altText, testidPrefix, heightPct }) {
  const [imgFailed, setImgFailed] = useState(false);

  // Reset imgFailed whenever imageUrl changes (e.g. index navigation between ads
  // reuses the same component instance — stale imgFailed=true would otherwise
  // block the new ad's image from rendering).
  useEffect(() => { setImgFailed(false); }, [imageUrl]);

  const hasImage = !!imageUrl && !imgFailed;

  const inner = hasImage ? (
    <img
      data-testid={`${testidPrefix}-image`}
      src={imageUrl}
      alt={altText || 'Advertisement'}
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      onError={() => setImgFailed(true)}
    />
  ) : (
    <div data-testid={`${testidPrefix}-placeholder`} style={{
      width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 10,
      background: 'linear-gradient(160deg, #F5F3EE 0%, #EDE9DF 100%)',
    }}>
      <div style={{ width: 52, height: 52, borderRadius: 12, background: 'rgba(196,150,48,0.15)', border: '1px solid rgba(196,150,48,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 22, opacity: 0.5 }}>◈</span>
      </div>
      <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: '#94A3B0' }}>
        Advertisement Reserved
      </div>
      <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 12, color: '#6B7F8C', textAlign: 'center', maxWidth: '55%' }}>
        Sponsor creative coming soon
      </div>
    </div>
  );

  return (
    <div style={{ width: '100%', height: heightPct, background: '#f4f1e8', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {ctaUrl ? (
        <a
          data-testid={`${testidPrefix}-image-link`}
          href={ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}
        >
          {inner}
        </a>
      ) : inner}
    </div>
  );
}

// Metadata block — used both as the compact side rail (full-page ads) and as a
// centered block stacked directly under each half-page creative (two-half ads).
function AdSideRailBlock({ label, sponsorName, disclosure, ctaUrl, ctaLabel, testidPrefix, centered = false }) {
  if (!sponsorName && !disclosure && !ctaUrl) return null;
  return (
    <div
      data-testid={`${testidPrefix}-rail-block`}
      style={centered ? { textAlign: 'center', padding: '0 12px' } : undefined}
    >
      {label && (
        <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 9, letterSpacing: 1.2, textTransform: 'uppercase', color: '#6B7F8C', marginBottom: 7 }}>
          {label}
        </div>
      )}
      {sponsorName && (
        <div data-testid={`${testidPrefix}-sponsor-name`} style={{ fontFamily: 'Archivo, sans-serif', fontSize: 14, fontWeight: 700, color: '#081D30', marginBottom: 4 }}>
          {sponsorName}
        </div>
      )}
      {disclosure && (
        <div data-testid={`${testidPrefix}-sponsor-disclosure`} style={{ fontFamily: 'Archivo, sans-serif', fontSize: 10, color: '#6B7F8C', lineHeight: 1.5, marginBottom: 12 }}>
          {disclosure}
        </div>
      )}
      {ctaUrl && (
        <a
          data-testid={`${testidPrefix}-cta-btn`}
          href={ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', fontFamily: 'Archivo, sans-serif', fontSize: 12.5,
            fontWeight: 700, padding: '10px 18px', borderRadius: 7, border: 'none',
            background: R.tealDeep, color: '#fff', textDecoration: 'none', letterSpacing: 0.2,
          }}
        >
          {ctaLabel || 'Learn More'} →
        </a>
      )}
    </div>
  );
}

function extractAlughaId(url) {
  if (!url) return null;
  const m = url.match(/alugha\.com\/(?:videos|embed)\/([a-f0-9-]+)/i);
  return m ? m[1] : null;
}

function AdPageCanvas({ article, selectedIdx, articlesCount, globalCurrentPage, totalGlobalPages, onPrevArticle, onNextArticle }) {
  const isHalf = article.ad_layout === 'two_half_page';
  const sponsorName = article.sponsor_name || '';
  const [videoOpen, setVideoOpen] = useState(false);
  const alughaId = extractAlughaId(article.video_url);

  return (
    <>
    <main data-testid="reader-center-canvas-ad" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#F5F3EE' }}>
      {/* Ad top bar */}
      <div style={{
        flex: '0 0 auto', background: '#FFFDF8', borderBottom: '1px solid #EDE9DF',
        padding: '9px 24px', display: 'flex', alignItems: 'center', position: 'relative',
      }}>
        <span style={{
          fontFamily: 'Archivo, sans-serif', fontSize: 9.5, fontWeight: 700,
          letterSpacing: 1.2, textTransform: 'uppercase',
          color: '#7A5612', background: '#FDF3D8', padding: '4px 10px', borderRadius: 20,
          flexShrink: 0,
        }}>
          Advertisement
        </span>
        <span style={{
          position: 'absolute', left: '50%', transform: 'translateX(-50%)',
          fontFamily: 'Archivo, sans-serif', fontSize: 11, color: '#6B7F8C',
          whiteSpace: 'nowrap', pointerEvents: 'none',
        }}>
          {sponsorName ? `Sponsored by ${sponsorName}` : `p.${article.pageNum || '—'}`}
        </span>
      </div>

      {/* Ad frame — fills full available height, zero vertical space */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 32px' }}>
        <div style={{ width: '100%', maxWidth: 560, margin: '0 auto', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div
            data-testid="reader-ad-page-frame"
            style={{
              flex: 1, minHeight: 0,
              borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E1D8',
              boxShadow: '0 8px 28px rgba(8,29,48,0.10)',
              display: 'flex', flexDirection: 'column', background: '#F8F5EE',
            }}
          >
            {isHalf ? (
              <>
                <AdImageSlot imageUrl={article.half_top_image_url} ctaUrl={article.half_top_cta_url} altText={article.half_top_sponsor_name} testidPrefix="reader-ad-half-top" heightPct="50%" />
                <div style={{ height: 1, background: '#EDE9DF', flexShrink: 0 }} />
                <AdImageSlot imageUrl={article.half_bottom_image_url} ctaUrl={article.half_bottom_cta_url} altText={article.half_bottom_sponsor_name} testidPrefix="reader-ad-half-bottom" heightPct="50%" />
              </>
            ) : (
              <>
                {/* Image fills all remaining height inside the frame */}
                <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                  <AdImageSlot imageUrl={article.page_image_url} ctaUrl={article.cta_url} altText={article.sponsor_name} testidPrefix="reader-ad" heightPct="100%" />
                </div>
                {/* CTA buttons — always render on full-page ads; inactive style when URL is empty */}
                <div data-testid="reader-ad-cta-buttons" style={{ flexShrink: 0, display: 'flex', gap: 8, padding: '10px 16px', background: '#F8F5EE', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button data-testid="reader-ad-visit-btn"
                    onClick={() => article.cta_url && window.open(article.cta_url, '_blank', 'noopener')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: '#081D30', border: '1px solid #081D30', color: '#FFFDF8', fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', cursor: article.cta_url ? 'pointer' : 'default', opacity: article.cta_url ? 1 : 0.4 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                    Visit Website
                  </button>
                  <button data-testid="reader-ad-video-btn"
                    onClick={() => alughaId && setVideoOpen(true)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: '#081D30', border: '1px solid #081D30', color: '#FFFDF8', fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', cursor: alughaId ? 'pointer' : 'default', opacity: alughaId ? 1 : 0.4 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
                    Watch Video
                  </button>
                  <button data-testid="reader-ad-cta-btn"
                    onClick={() => article.qr_destination_url && window.open(article.qr_destination_url, '_blank', 'noopener')}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: 'transparent', border: '1px solid #081D30', color: '#081D30', fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', cursor: article.qr_destination_url ? 'pointer' : 'default', opacity: article.qr_destination_url ? 1 : 0.4 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    Connect With Us
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Half-page sponsor info removed — no metadata renders below half-page ads */}
        </div>
      </div>

      {/* Bottom nav */}
      <div style={{ flex: '0 0 auto', background: '#FFFDF8', borderTop: '1px solid #EDE9DF', padding: '10px 24px', display: 'flex', alignItems: 'center' }}>
        {/* Left spacer — mirrors the Back+Next button group width so the page counter stays truly centered */}
        <div style={{ flex: 1 }} />
        {/* Center: global page indicator */}
        <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#6B7F8C', letterSpacing: 0.3, flexShrink: 0 }}>
          {globalCurrentPage != null && totalGlobalPages != null
            ? `Page ${globalCurrentPage} of ${totalGlobalPages}`
            : `Page ${selectedIdx + 1} of ${articlesCount}`}
        </span>
        {/* Right: Back + Next together */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <button data-testid="reader-ad-prev-btn" onClick={onPrevArticle} disabled={selectedIdx === 0}
            style={{ fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 600, padding: '8px 16px', borderRadius: 6, border: '1px solid #D8D3C8', background: '#fff', color: selectedIdx === 0 ? '#94A3B0' : '#081D30', cursor: selectedIdx === 0 ? 'default' : 'pointer', opacity: selectedIdx === 0 ? 0.38 : 1 }}>
            ← Back
          </button>
          <button data-testid="reader-ad-next-btn" onClick={onNextArticle} disabled={selectedIdx >= articlesCount - 1}
            style={{ fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 600, padding: '8px 16px', borderRadius: 6, border: selectedIdx >= articlesCount - 1 ? '1px solid #D8D3C8' : '1px solid #081D30', background: selectedIdx >= articlesCount - 1 ? '#fff' : '#081D30', color: selectedIdx >= articlesCount - 1 ? '#94A3B0' : '#FFFDF8', cursor: selectedIdx >= articlesCount - 1 ? 'default' : 'pointer', opacity: selectedIdx >= articlesCount - 1 ? 0.38 : 1 }}>
            Next →
          </button>
        </div>
      </div>
    </main>

    {/* Alugha Video Lightbox */}
    {videoOpen && alughaId && (
      <div
        onClick={() => setVideoOpen(false)}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.87)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{ position: 'relative', width: 860, maxWidth: '95vw' }}
        >
          {/* Close button */}
          <button
            onClick={() => setVideoOpen(false)}
            style={{
              position: 'absolute', top: -44, right: 0,
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '50%', width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff', fontSize: 18, lineHeight: 1,
              backdropFilter: 'blur(4px)',
            }}
            aria-label="Close video"
          >
            ✕
          </button>
          <iframe
            src={`https://alugha.com/embed/web-player?v=${alughaId}`}
            width="860"
            height="480"
            style={{ display: 'block', width: '100%', height: 480, border: 'none', borderRadius: 8 }}
            allow="autoplay; fullscreen"
            allowFullScreen
            title="Advertiser Video"
          />
        </div>
      </div>
    )}
    </>
  );
}



// ─── Right Rail ───────────────────────────────────────────────────────────────
function RightRail({ article, issue, unlocked, onUnlock, innerRef, gateReason }) {
  return (
    <aside
      data-testid="reader-right-rail"
      ref={innerRef}
      style={{
        width: 256, minWidth: 256, background: R.rail, borderLeft: `1px solid ${R.railBorder}`,
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}
    >
      {/* Issue metadata */}
      <div style={{ padding: '16px 14px 12px', borderBottom: `1px solid ${R.railDivider}` }}>
        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, letterSpacing: 1.2, textTransform: 'uppercase', color: R.railSub, marginBottom: 10 }}>
          Issue Info
        </div>
        {[
          ['Edition', 'Jan/Feb 2026'],
          ['Volume', issue?.volume_number ? `VOL. ${issue.volume_number}` : 'VOL. 6'],
          ['Pages', `${issue?.page_count || 40}`],
          ['Online', `${issue?.articles_online || 6} articles`],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${R.railDivider}` }}>
            <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.railSub }}>{k}</span>
            <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.railText }}>{v}</span>
          </div>
        ))}
      </div>

      {/* Now reading */}
      {article && (
        <div style={{ padding: '14px', borderBottom: `1px solid ${R.railDivider}` }}>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, letterSpacing: 1.2, textTransform: 'uppercase', color: R.railSub, marginBottom: 8 }}>
            Now Reading
          </div>
          <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 700, color: R.railText, lineHeight: 1.4, marginBottom: 5 }}>
            {article.title}
          </div>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9.5, color: R.railAccent, textTransform: 'capitalize', marginBottom: 6 }}>
            {capitalize(article.primary_pillar || 'Editorial')}
          </div>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.railSub }}>
            {readTime(article.body)}
          </div>
        </div>
      )}

      {/* Unlock gate */}
      {!unlocked ? (
        <div style={{ padding: '14px', borderBottom: `1px solid ${R.railDivider}` }}>
          <div style={{
            background: 'rgba(47,148,134,0.07)', border: '1px solid rgba(47,148,134,0.2)',
            borderRadius: 9, padding: '14px',
          }}>
            <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 13, fontWeight: 700, color: R.railText, marginBottom: 6, lineHeight: 1.3 }}>
              Get Free Reading Access
            </div>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.railSub, lineHeight: 1.6, marginBottom: 14 }}>
              Subscribe free to unlock the full digital edition.
            </div>
            <a
              data-testid="reader-gate-unlock-btn"
              href="/subscribe?product=magazine"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'block', width: '100%', padding: '9px', borderRadius: 6, border: 'none',
                background: R.tealDeep, color: '#fff', fontFamily: 'Archivo, sans-serif',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3,
                textDecoration: 'none', textAlign: 'center', boxSizing: 'border-box',
              }}
            >
              Subscribe Free →
            </a>
          </div>
        </div>
      ) : (
        <div style={{ padding: '14px', borderBottom: `1px solid ${R.railDivider}` }}>
          <div style={{
            background: 'rgba(47,148,134,0.12)', border: `1px solid ${R.tealDeep}`,
            borderRadius: 7, padding: '12px 14px', textAlign: 'center',
            fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: R.railAccent,
          }}>
            Full access unlocked
          </div>
        </div>
      )}
    </aside>
  );
}

// ─── Mobile width detector ────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [mob, setMob] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const check = () => setMob(window.innerWidth < bp);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [bp]);
  return mob;
}

// ─── Mobile Unlock Box ────────────────────────────────────────────────────────
function MobileUnlockBox({ onUnlock }) {
  return (
    <div>
      <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.metaText, lineHeight: 1.6, marginBottom: 12 }}>
        Subscribe free to read every article in this issue — and every issue going forward.
      </div>
      <a
        data-testid="mobile-gate-unlock-btn"
        href="/subscribe?product=magazine"
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: 'block', width: '100%', padding: '11px', borderRadius: 6, border: 'none', background: R.tealDeep, color: '#fff', fontFamily: 'Archivo, sans-serif', fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3, textDecoration: 'none', textAlign: 'center', boxSizing: 'border-box' }}
      >
        Subscribe Free →
      </a>
    </div>
  );
}

// ─── Mobile Ad / Sponsor Block ────────────────────────────────────────────────
// Same 842:1191 portrait frame governance as desktop; metadata collapses below
// the frame on mobile (per spec) instead of a side rail.
function MobileAdImageSlot({ imageUrl, ctaUrl, altText, testidPrefix, heightPct }) {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [imageUrl]);
  const hasImage = !!imageUrl && !imgFailed;
  const inner = hasImage ? (
    <img data-testid={`${testidPrefix}-image`} src={imageUrl} alt={altText || 'Advertisement'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={() => setImgFailed(true)} />
  ) : (
    <div data-testid={`${testidPrefix}-placeholder`} style={{ textAlign: 'center', padding: '0 12px', fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.railSub }}>
      Sponsor placement reserved
    </div>
  );
  return (
    <div style={{ width: '100%', height: heightPct, background: '#f4f1e8', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {ctaUrl ? (
        <a data-testid={`${testidPrefix}-image-link`} href={ctaUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>{inner}</a>
      ) : inner}
    </div>
  );
}

function MobileAdMeta({ label, sponsorName, disclosure, ctaUrl, ctaLabel, testidPrefix }) {
  if (!sponsorName && !disclosure && !ctaUrl) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: R.railSub, marginBottom: 4 }}>{label}</div>}
      {sponsorName && <div data-testid={`${testidPrefix}-sponsor-name`} style={{ fontFamily: 'Archivo, sans-serif', fontSize: 13, fontWeight: 700, color: R.navyText, marginBottom: 4 }}>{sponsorName}</div>}
      {disclosure && <div data-testid={`${testidPrefix}-sponsor-disclosure`} style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9.5, color: R.metaText, marginBottom: 8 }}>{disclosure}</div>}
      {ctaUrl && (
        <a data-testid={`${testidPrefix}-cta-btn`} href={ctaUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 700, padding: '9px 14px', borderRadius: 7, background: R.tealDeep, color: '#fff', textDecoration: 'none' }}>
          {ctaLabel || 'Learn More'} →
        </a>
      )}
    </div>
  );
}

function MobileAdBlock({ article }) {
  const isHalf = article.ad_layout === 'two_half_page';
  const [videoOpen, setVideoOpen] = useState(false);
  const alughaId = extractAlughaId(article.video_url);

  return (
    <>
    <article style={{ padding: '18px 16px 20px', maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* True 842:1191 portrait frame — metadata collapses below, outside the frame */}
      <div
        data-testid="reader-ad-page-frame"
        style={{
          width: '100%', maxWidth: 320, margin: '0 auto 16px', aspectRatio: '842 / 1191',
          borderRadius: 6, overflow: 'hidden', border: `1px solid ${R.divider}`, background: '#f4f1e8',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {isHalf ? (
          <>
            <MobileAdImageSlot imageUrl={article.half_top_image_url} ctaUrl={article.half_top_cta_url} altText={article.half_top_sponsor_name} testidPrefix="reader-ad-half-top" heightPct="50%" />
            <div style={{ height: 1, background: R.divider, flexShrink: 0 }} />
            <MobileAdImageSlot imageUrl={article.half_bottom_image_url} ctaUrl={article.half_bottom_cta_url} altText={article.half_bottom_sponsor_name} testidPrefix="reader-ad-half-bottom" heightPct="50%" />
          </>
        ) : (
          <MobileAdImageSlot imageUrl={article.page_image_url} ctaUrl={article.cta_url} altText={article.sponsor_name} testidPrefix="reader-ad" heightPct="100%" />
        )}
      </div>

      {/* 3-button row for full-page ads — identical fields to desktop AdPageCanvas */}
      {!isHalf && (
        <div data-testid="reader-ad-cta-buttons" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button data-testid="reader-ad-visit-btn"
            onClick={() => article.cta_url && window.open(article.cta_url, '_blank', 'noopener')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 13px', borderRadius: 6, background: '#081D30', border: '1px solid #081D30', color: '#FFFDF8', fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', cursor: article.cta_url ? 'pointer' : 'default', opacity: article.cta_url ? 1 : 0.4 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            Visit Website
          </button>
          <button data-testid="reader-ad-video-btn"
            onClick={() => alughaId && setVideoOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 13px', borderRadius: 6, background: '#081D30', border: '1px solid #081D30', color: '#FFFDF8', fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', cursor: alughaId ? 'pointer' : 'default', opacity: alughaId ? 1 : 0.4 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 19,12 5,21"/></svg>
            Watch Video
          </button>
          <button data-testid="reader-ad-cta-btn"
            onClick={() => article.qr_destination_url && window.open(article.qr_destination_url, '_blank', 'noopener')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 13px', borderRadius: 6, background: 'transparent', border: '1px solid #081D30', color: '#081D30', fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', cursor: article.qr_destination_url ? 'pointer' : 'default', opacity: article.qr_destination_url ? 1 : 0.4 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Connect With Us
          </button>
        </div>
      )}
    </article>

    {/* Alugha Video Lightbox — mirrors desktop AdPageCanvas */}
    {videoOpen && alughaId && (
      <div
        onClick={() => setVideoOpen(false)}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.87)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{ position: 'relative', width: 860, maxWidth: '95vw' }}
        >
          <button
            onClick={() => setVideoOpen(false)}
            style={{
              position: 'absolute', top: -44, right: 0,
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '50%', width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#fff', fontSize: 18, lineHeight: 1,
              backdropFilter: 'blur(4px)',
            }}
            aria-label="Close video"
          >
            ✕
          </button>
          <iframe
            src={`https://alugha.com/embed/web-player?v=${alughaId}`}
            width="860"
            height="480"
            style={{ display: 'block', width: '100%', height: 480, border: 'none', borderRadius: 8 }}
            allow="autoplay; fullscreen"
            allowFullScreen
            title="Advertiser Video"
          />
        </div>
      </div>
    )}
    </>
  );
}

// ─── Mobile Reader Layout ─────────────────────────────────────────────────────
// Standalone helper — same packing algorithm as runMobilePaginate but returns a
// count only. Used for eager pre-measurement of all articles so the global
// page counter (bottom bar) reflects the TRUE total.
function measureMobilePageCount(art, contentW, frameH, mDiv) {
  if (!art?.body || frameH < 60 || contentW < 60) return 1;
  const fontSize = 15;
  const PAD_TOP  = 18;
  const SAFETY   = 10;

  mDiv.style.cssText = [
    'position:fixed','top:-9999px','left:0','visibility:hidden',
    'pointer-events:none','z-index:-1',
    `width:${contentW}px`,
    "font-family:'Source Serif 4',serif",
    `font-size:${fontSize}px`,
    'line-height:1.6',
  ].join(';') + ';';

  const hasImg = !!(art.hero_image_url || art.featured_image_url);
  const imgH   = hasImg ? contentW * (9 / 16) + 14 : 0;

  mDiv.innerHTML = `<h1 style="margin:0 0 8px;font-family:'Archivo',sans-serif;font-size:20px;line-height:1.2;font-weight:800;">${escTxt(art.title)}</h1>`;
  const titleH = mDiv.scrollHeight + 8;

  let dekH = 0;
  if (art.dek) {
    mDiv.innerHTML = `<p style="margin:0 0 10px;font-family:'Source Serif 4',serif;font-size:14px;line-height:1.5;font-style:italic;">${escTxt(art.dek)}</p>`;
    dekH = mDiv.scrollHeight + 10;
  }
  const metaH   = 38;
  const bodyGap = 14;
  const headerH = PAD_TOP + imgH + titleH + dekH + metaH + bodyGap;

  const BUDGET_0 = Math.max(120, frameH - headerH - SAFETY);
  const BUDGET_N = Math.max(120, frameH - PAD_TOP - SAFETY);

  const domDiv = document.createElement('div');
  domDiv.innerHTML = DOMPurify.sanitize(art.body);
  const bodyBlocks = Array.from(domDiv.children).map(el => el.outerHTML).filter(Boolean);
  if (!bodyBlocks.length) return 1;

  const blockH = bodyBlocks.map(html => {
    mDiv.innerHTML = `<div style="font-family:'Source Serif 4',serif;font-size:${fontSize}px;line-height:1.6;">${html}</div>`;
    return Math.max(1, mDiv.scrollHeight);
  });

  let count = 0, bIdx = 0, isFirst = true;
  while (bIdx < bodyBlocks.length) {
    const budget = isFirst ? BUDGET_0 : BUDGET_N;
    const pg = []; let used = 0;
    while (bIdx < bodyBlocks.length) {
      const h = blockH[bIdx], html = bodyBlocks[bIdx];
      const isHdg  = /^<h[1-6][\s>]/i.test(html.trim());
      const nextH  = bIdx + 1 < blockH.length ? blockH[bIdx + 1] : 0;
      const needed = isHdg ? h + nextH : h;
      if (pg.length > 0 && used + needed > budget) break;
      pg.push(html); used += h; bIdx++;
    }
    if (pg.length === 0 && bIdx < bodyBlocks.length) bIdx++;
    count++; isFirst = false;
  }
  return Math.max(1, count);
}

function MobileReaderLayout({
  issue, articles, selectedIdx, handleSelect,
  drawerOpen, setDrawerOpen,
}) {
  const article = articles[selectedIdx];

  // ── Pagination state ─────────────────────────────────────────────────────
  const [mobilePageIdx, setMobilePageIdx] = useState(0);
  const [mobilePages,   setMobilePages]   = useState([]);
  const hiddenRef      = useRef(null);

  // ── Measured content zone height (set by ResizeObserver) ─────────────────
  // The content zone is the flex:1 div below the pillar badge bar.
  // Using its measured height ensures the pagination algorithm uses the
  // EXACT available space, preventing overflow into the bottom nav.
  const contentAreaRef = useRef(null);
  const [contentH, setContentH] = useState(0);

  // ── Pre-computed page counts for ALL articles (for global counter) ────────
  const [articlePageCounts, setArticlePageCounts] = useState({});

  // ── Drawer filter state ───────────────────────────────────────────────────
  const [filterCategory, setFilterCategory] = useState(null);

  // ── Audio playback state ─────────────────────────────────────────────────
  const audioRef       = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);

  // Stop audio and reset when article changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setAudioPlaying(false);
  }, [article?.id]); // eslint-disable-line

  // Auto page flip: advance page based on audio progress
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !audioPlaying) return;
    const onTimeUpdate = () => {
      const dur = el.duration;
      if (!dur || totalMobPages <= 1) return;
      const targetPage = Math.min(
        totalMobPages - 1,
        Math.floor((el.currentTime / dur) * totalMobPages)
      );
      if (targetPage > mobilePageIdx) setMobilePageIdx(targetPage);
    };
    el.addEventListener('timeupdate', onTimeUpdate);
    return () => el.removeEventListener('timeupdate', onTimeUpdate);
  }); // run on every render to pick up fresh totalMobPages / mobilePageIdx values

  // ── Swipe detection ───────────────────────────────────────────────────────
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // ── Track actual content zone height via ResizeObserver ──────────────────
  useEffect(() => {
    const el = contentAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const h = entries[0]?.contentRect?.height;
      if (h && h > 60) setContentH(Math.floor(h));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Paginate current article ──────────────────────────────────────────────
  const fontSize = 15;
  const lineH    = 1.6;

  const runMobilePaginate = useCallback(() => {
    const mDiv = hiddenRef.current;
    if (!mDiv || !article?.body || article._isAd || article._isCover || contentH < 60) {
      setMobilePages([]);
      return;
    }

    const contentW = Math.max(200, window.innerWidth - 32);
    const frameH   = contentH;   // precise measured height
    const PAD_TOP  = 18;
    const SAFETY   = 12;         // extra safety gap to stay clear of nav bar

    mDiv.style.cssText = [
      'position:fixed','top:-9999px','left:0','visibility:hidden',
      'pointer-events:none','z-index:-1',
      `width:${contentW}px`,
      "font-family:'Source Serif 4',serif",
      `font-size:${fontSize}px`,
      `line-height:${lineH}`,
    ].join(';') + ';';

    // ── Header overhead (page 0 only) ────────────────────────────────────
    const hasImg = !!(article.hero_image_url || article.featured_image_url);
    const imgH   = hasImg ? contentW * (9 / 16) + 14 : 0;

    mDiv.innerHTML = `<h1 style="margin:0 0 8px;font-family:'Archivo',sans-serif;font-size:20px;line-height:1.2;font-weight:800;">${escTxt(article.title)}</h1>`;
    const titleH = mDiv.scrollHeight + 8;

    let dekH = 0;
    if (article.dek) {
      mDiv.innerHTML = `<p style="margin:0 0 10px;font-family:'Source Serif 4',serif;font-size:14px;line-height:1.5;font-style:italic;">${escTxt(article.dek)}</p>`;
      dekH = mDiv.scrollHeight + 10;
    }
    const metaH    = 38;
    const bodyGap  = 14;
    const headerH  = PAD_TOP + imgH + titleH + dekH + metaH + bodyGap;

    // Minimum BUDGET_0 of 120px ensures opening body text always appears
    const BUDGET_0 = Math.max(120, frameH - headerH - SAFETY);
    const BUDGET_N = Math.max(120, frameH - PAD_TOP - SAFETY);

    const domDiv = document.createElement('div');
    domDiv.innerHTML = DOMPurify.sanitize(article.body);
    const bodyBlocks = Array.from(domDiv.children).map(el => el.outerHTML).filter(Boolean);
    if (!bodyBlocks.length) { setMobilePages(['']); return; }

    const blockH = bodyBlocks.map(html => {
      mDiv.innerHTML = `<div style="font-family:'Source Serif 4',serif;font-size:${fontSize}px;line-height:${lineH};">${html}</div>`;
      return Math.max(1, mDiv.scrollHeight);
    });

    const newPages = [];
    let bIdx = 0, isFirst = true;
    while (bIdx < bodyBlocks.length) {
      const budget = isFirst ? BUDGET_0 : BUDGET_N;
      const pg = []; let used = 0;
      while (bIdx < bodyBlocks.length) {
        const h    = blockH[bIdx];
        const html = bodyBlocks[bIdx];
        const isHdg  = /^<h[1-6][\s>]/i.test(html.trim());
        const nextH  = bIdx + 1 < blockH.length ? blockH[bIdx + 1] : 0;
        const needed = isHdg ? h + nextH : h;
        if (pg.length > 0 && used + needed > budget) break;
        pg.push(html); used += h; bIdx++;
      }
      if (pg.length === 0 && bIdx < bodyBlocks.length) { pg.push(bodyBlocks[bIdx]); bIdx++; }
      newPages.push(pg.join(''));
      isFirst = false;
    }
    setMobilePages(newPages.length ? newPages : ['']);
  }, [article?.id, article?.body, article?._isAd, article?._isCover, contentH]); // eslint-disable-line

  useLayoutEffect(() => { runMobilePaginate(); }, [runMobilePaginate]);
  useEffect(() => { document.fonts.ready.then(runMobilePaginate); }, [runMobilePaginate]);

  // Reset page index when article changes
  useEffect(() => { setMobilePageIdx(0); }, [article?.id]); // eslint-disable-line

  // ── Eagerly measure ALL articles once contentH is known ──────────────────
  useEffect(() => {
    const mDiv = hiddenRef.current;
    if (!mDiv || contentH < 60) return;
    const contentW = Math.max(200, window.innerWidth - 32);
    const counts = {};
    articles.forEach(art => {
      counts[art.id] = (art._isAd || art._isCover || !art.body)
        ? 1
        : measureMobilePageCount(art, contentW, contentH, mDiv);
    });
    setArticlePageCounts(counts);
  }, [articles, contentH]); // eslint-disable-line

  // ── Derived ────────────────────────────────────────────────────────────────
  const isAdOrCover   = !!(article?._isAd || article?._isCover);
  const totalMobPages = isAdOrCover ? 1 : Math.max(1, mobilePages.length);
  const isFirst       = selectedIdx === 0 && mobilePageIdx === 0;
  const isLast        = selectedIdx >= articles.length - 1 && mobilePageIdx >= totalMobPages - 1;

  // Global page counter — uses pre-measured counts for all articles
  const globalTotalPages = articles.reduce(
    (sum, art) => sum + (articlePageCounts[art.id] || 1), 0
  );
  const globalCurrentPage = articles.slice(0, selectedIdx).reduce(
    (sum, art) => sum + (articlePageCounts[art.id] || 1), 0
  ) + mobilePageIdx + 1;

  const handleNext = () => {
    if (!isAdOrCover && mobilePageIdx < totalMobPages - 1) {
      setMobilePageIdx(p => p + 1);
    } else if (selectedIdx < articles.length - 1) {
      handleSelect(selectedIdx + 1);
    }
  };
  const handlePrev = () => {
    if (!isAdOrCover && mobilePageIdx > 0) {
      setMobilePageIdx(p => p - 1);
    } else if (selectedIdx > 0) {
      handleSelect(selectedIdx - 1);
    }
  };

  // ── Swipe handlers ────────────────────────────────────────────────────────
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) handleNext();
      else handlePrev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  // ── Drawer article list with category filter ──────────────────────────────
  const displayArticles = useMemo(() => {
    const indexed = articles.map((art, idx) => ({ art, idx }));
    if (!filterCategory) return indexed;
    return [...indexed].sort((a, b) => {
      const cat = art => !art._isAd && !art._isCover
        ? capitalize(art.primary_pillar || art.pillar || 'Editorial') : null;
      const aM = cat(a.art) === filterCategory ? 1 : 0;
      const bM = cat(b.art) === filterCategory ? 1 : 0;
      return bM - aM;
    });
  }, [articles, filterCategory]); // eslint-disable-line

  // ── Body page content to render ───────────────────────────────────────────
  const currentPageHtml = !isAdOrCover
    ? DOMPurify.sanitize(mobilePages[mobilePageIdx] || '')
    : '';
  const showHeader = mobilePageIdx === 0;

  return (
    <>
    {/* Hidden measurement sandbox */}
    <div ref={hiddenRef} aria-hidden="true" style={{ position: 'fixed', top: '-9999px', left: 0, visibility: 'hidden', pointerEvents: 'none', zIndex: -1 }} />
    {/* HTML5 audio element — controlled by Listen button */}
    {article?.audio_url && (
      <audio
        ref={audioRef}
        src={article.audio_url}
        preload="metadata"
        onPlay={() => setAudioPlaying(true)}
        onPause={() => setAudioPlaying(false)}
        onEnded={() => setAudioPlaying(false)}
        style={{ display: 'none' }}
      />
    )}

    <div data-testid="mobile-reader-root" style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: R.outer, overflow: 'hidden' }}>

      {/* ── Compact top bar ─────────────────────────────────────── */}
      <div
        data-testid="mobile-reader-top-bar"
        style={{ flexShrink: 0, height: 52, background: R.rail, borderBottom: `1px solid ${R.railBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', zIndex: 20, gap: 8 }}
      >
        <Link
          to="/magazine"
          data-testid="mobile-reader-back-link"
          style={{ flexShrink: 0, fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.railAccent, textDecoration: 'none', letterSpacing: 0.3 }}
        >
          ← Back
        </Link>
        <span style={{ flex: 1, fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: 700, color: R.railText, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 6px' }}>
          {issue?.issue_date || 'Opportunity Post'}
        </span>
        <button
          data-testid="mobile-reader-articles-btn"
          onClick={() => setDrawerOpen(true)}
          style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 10px', borderRadius: 6, border: `1px solid ${R.railBorder}`, background: 'transparent', color: R.railText, fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, cursor: 'pointer', letterSpacing: 0.3 }}
        >
          In This Issue ☰
        </button>
      </div>

      {/* ── Drawer backdrop ──────────────────────────────────────── */}
      <div
        data-testid="mobile-reader-drawer-overlay"
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
        style={{ position: 'fixed', inset: 0, top: 52, background: 'rgba(5,10,15,0.6)', zIndex: 40, pointerEvents: drawerOpen ? 'all' : 'none', opacity: drawerOpen ? 1 : 0, transition: 'opacity 0.2s ease' }}
      />

      {/* ── In This Issue drawer — full-screen width ─────────────── */}
      <div
        data-testid="mobile-reader-drawer"
        style={{ position: 'fixed', top: 52, left: 0, width: '100vw', height: 'calc(100vh - 52px)', background: R.rail, zIndex: 50, transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.25s ease', overflowY: 'auto', display: 'flex', flexDirection: 'column', pointerEvents: drawerOpen ? 'all' : 'none' }}
      >
        <div style={{ flexShrink: 0, padding: '14px 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${R.railDivider}` }}>
          <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, letterSpacing: 1.2, textTransform: 'uppercase', color: R.railSub }}>In This Issue</span>
          <button
            data-testid="mobile-reader-drawer-close"
            onClick={() => setDrawerOpen(false)}
            style={{ width: 32, height: 32, borderRadius: 6, border: 'none', background: 'transparent', color: R.railSub, fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
          >✕</button>
        </div>
        {filterCategory && (
          <div style={{ flexShrink: 0, padding: '8px 16px', background: R.tealBadge, borderBottom: `1px solid ${R.railDivider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, color: R.tealDeep, fontWeight: 700 }}>Filtered: {filterCategory}</span>
            <button onClick={() => setFilterCategory(null)} style={{ border: 'none', background: 'transparent', color: R.tealDeep, fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, cursor: 'pointer', padding: 0 }}>Clear ✕</button>
          </div>
        )}
        {issue?.cover_image_url && (
          <div style={{ padding: '14px 16px 12px', borderBottom: `1px solid ${R.railDivider}` }}>
            <div style={{ width: '50%', aspectRatio: '3/4', borderRadius: 6, overflow: 'hidden', border: `1px solid ${R.railBorder}` }}>
              <img src={issue.cover_image_url} alt="Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          </div>
        )}
        {displayArticles.map(({ art, idx }) => {
          const active  = idx === selectedIdx;
          const artCat  = !art._isAd && !art._isCover ? capitalize(art.primary_pillar || art.pillar || 'Editorial') : null;
          const dimmed  = filterCategory && artCat !== filterCategory && !art._isAd && !art._isCover;
          return (
            <div
              key={art.id}
              role="button"
              tabIndex={0}
              data-testid={`mobile-toc-item-${idx}`}
              onClick={() => { handleSelect(idx); setDrawerOpen(false); }}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { handleSelect(idx); setDrawerOpen(false); } }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '16px 16px 16px 13px', border: 'none', cursor: 'pointer', background: active ? R.railActive : 'transparent', borderLeft: `3px solid ${active ? R.tealDeep : 'transparent'}`, borderBottom: `1px solid ${R.railDivider}`, transition: 'background 0.15s ease', opacity: dimmed ? 0.45 : 1, boxSizing: 'border-box' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {art._isAd && (
                    <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, fontWeight: 700, color: '#b98a2e', background: '#fbeecb', padding: '2px 7px', borderRadius: 3 }}>AD</span>
                  )}
                  {artCat && (
                    <button
                      data-testid={`mobile-toc-cat-${idx}`}
                      onClick={e => { e.stopPropagation(); setFilterCategory(f => f === artCat ? null : artCat); }}
                      style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 8.5, fontWeight: 600, background: filterCategory === artCat ? R.tealDeep : R.tealBadge, padding: '2px 7px', borderRadius: 3, border: 'none', cursor: 'pointer', letterSpacing: 0.3, textTransform: 'uppercase', transition: 'background 0.15s ease', color: filterCategory === artCat ? '#fff' : R.tealBadgeText }}
                    >
                      {artCat}
                    </button>
                  )}
                </div>
                <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, color: active ? R.railAccent : R.railSub, fontWeight: 600 }}>
                  p.{art.pageNum || '—'}
                </span>
              </div>
              <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 13, fontWeight: active ? 700 : 500, color: active ? '#fff' : R.railText, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {art._isAd ? (art.title !== 'Advertisement' ? art.title : null) : art.title}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Main content area ─────────────────────────────────────── */}
      <div
        style={{ flex: 1, overflow: 'hidden', background: R.canvas, display: 'flex', flexDirection: 'column' }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Pillar badge bar — dot indicators replace X/Y text counter */}
        <div style={{ flexShrink: 0, padding: '7px 14px', background: R.canvasBar, borderBottom: `1px solid ${R.divider}`, display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
          <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', color: article?._isAd ? '#8a6412' : article?._isCover ? R.railSub : R.tealBadgeText, background: article?._isAd ? '#fbeecb' : article?._isCover ? 'transparent' : R.tealBadge, padding: '3px 8px', borderRadius: 4 }}>
            {article?._isAd ? 'Advertisement' : article?._isCover ? 'Cover' : capitalize(article?.primary_pillar || 'Editorial')}
          </span>
          {audioPlaying && (
            <span style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
              <EqBars />
            </span>
          )}
          {totalMobPages > 1 && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
              {Array.from({ length: totalMobPages }, (_, i) => (
                <div
                  key={i}
                  style={{
                    width:       i === mobilePageIdx ? 7 : 5,
                    height:      i === mobilePageIdx ? 7 : 5,
                    borderRadius: '50%',
                    background:  i === mobilePageIdx ? R.tealDeep : 'transparent',
                    border:      `1.5px solid ${i === mobilePageIdx ? R.tealDeep : R.btnBorder}`,
                    transition:  'all 0.2s ease',
                    flexShrink:  0,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Page content — hard boundary, fills exact remaining height */}
        <div ref={contentAreaRef} style={{ flex: 1, overflow: 'hidden' }}>
          {article?._isCover ? (
            <div
              data-testid="mobile-cover-page"
              style={{ width: '100%', height: '100%', background: '#0d1a24', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}
            >
              {article.cover_image_url ? (
                <img
                  src={article.cover_image_url}
                  alt="Magazine Cover"
                  style={{ maxHeight: '90%', maxWidth: '92%', objectFit: 'contain', display: 'block', borderRadius: 3 }}
                />
              ) : (
                <div style={{ width: '60%', aspectRatio: '2/3', background: '#1c2d3c', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontFamily: 'Newsreader, Georgia, serif', fontSize: 15, color: '#FFFDF8', textAlign: 'center', padding: '0 16px' }}>{article.title}</span>
                </div>
              )}
              {!isLast && (
                <button
                  data-testid="mobile-cover-start-btn"
                  onClick={handleNext}
                  style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', padding: '10px 28px', borderRadius: 7, background: '#FFFDF8', color: '#081D30', border: 'none', fontFamily: 'Archivo, sans-serif', fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3, whiteSpace: 'nowrap', boxShadow: '0 2px 16px rgba(0,0,0,0.55)' }}
                >
                  Start Reading →
                </button>
              )}
            </div>
          ) : article?._isAd ? (
            <div style={{ height: '100%', overflow: 'hidden' }}>
              <MobileAdBlock article={article} />
            </div>
          ) : article ? (
            <div style={{ height: '100%', overflow: 'hidden', padding: '18px 16px 0', boxSizing: 'border-box' }}>
              {/* Page 0: header (hero + title + dek + meta) */}
              {showHeader && (
                <>
                  <div style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: 7, overflow: 'hidden', background: 'repeating-linear-gradient(45deg,#d4d1c1 0,#d4d1c1 15px,#dcd9ca 15px,#dcd9ca 30px)', border: `1px solid ${R.divider}`, marginBottom: 14, flexShrink: 0 }}>
                    {(article.hero_image_url || article.featured_image_url) && (
                      <img
                        src={article.hero_image_url || article.featured_image_url}
                        alt={article.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <span style={{ position: 'absolute', bottom: 8, left: 8, fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, letterSpacing: 1, color: R.tealBadgeText, background: R.tealBadge, padding: '3px 7px', borderRadius: 4, textTransform: 'uppercase' }}>
                      {capitalize(article.primary_pillar || 'editorial')}
                    </span>
                  </div>
                  <h1 data-testid="reader-article-title" style={{ margin: '0 0 8px', fontFamily: 'Archivo, sans-serif', fontSize: 20, lineHeight: 1.2, fontWeight: 800, letterSpacing: -0.3, color: R.navyText }}>
                    {article.title}
                  </h1>
                  {article.dek && (
                    <p style={{ margin: '0 0 10px', fontFamily: 'Source Serif 4, serif', fontSize: 14, lineHeight: 1.5, fontStyle: 'italic', color: R.deikText }}>
                      {article.dek}
                    </p>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.metaText, paddingBottom: 12, borderBottom: `1px solid ${R.divider}`, marginBottom: 14, alignItems: 'center' }}>
                    <span style={{ color: R.tealText, fontWeight: 600 }}>{article.byline || 'By Opportunity Post Editorial Team'}</span>
                    <span>{readTime(article.body)}</span>
                    <button
                        data-testid="mobile-reader-listen-btn"
                        onClick={() => {
                          const el = audioRef.current;
                          if (!el || !article.audio_url) return;
                          if (audioPlaying) { el.pause(); } else { el.play(); }
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, background: 'transparent', border: `1px solid ${article.audio_url ? '#C9A227' : 'rgba(8,29,48,0.18)'}`, color: article.audio_url ? '#C9A227' : 'rgba(8,29,48,0.35)', fontFamily: 'IBM Plex Mono, monospace', fontSize: 9, fontWeight: 700, letterSpacing: 0.4, cursor: article.audio_url ? 'pointer' : 'default', flexShrink: 0, marginLeft: 'auto' }}
                      >
                        {audioPlaying ? 'Pause' : 'Listen'}
                      </button>
                  </div>
                </>
              )}
              {/* Body content for this page */}
              <div
                data-testid="reader-article-body"
                style={{ fontFamily: 'Source Serif 4, serif', fontSize: fontSize, lineHeight: lineH, color: R.bodyText }}
              >
                <style>{`.op-reader-body h2{font-family:Archivo,sans-serif;font-size:16px;font-weight:700;color:${R.navyText};margin:16px 0 8px}.op-reader-body p{margin:0 0 0.85em}.op-reader-body a{color:${R.tealText}}.op-reader-body img{max-width:100%;height:auto}`}</style>
                <div className="op-reader-body" dangerouslySetInnerHTML={{ __html: currentPageHtml }} />
              </div>
            </div>
          ) : (
            <div style={{ padding: '48px 16px', textAlign: 'center' }}>
              <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: R.mutedText }}>Select an article to start reading</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom nav — global true page counter ────────────────── */}
      <div
        data-testid="mobile-reader-bottom-bar"
        style={{ flexShrink: 0, padding: '10px 14px', background: R.canvasBar, borderTop: `1px solid ${R.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        <button
          data-testid="mobile-reader-prev-btn"
          onClick={handlePrev}
          disabled={isFirst}
          style={{ flex: 1, padding: '10px 12px', borderRadius: 7, border: `1px solid ${R.btnBorder}`, background: '#fff', color: R.navyText, fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 600, cursor: isFirst ? 'default' : 'pointer', opacity: isFirst ? 0.3 : 1, textAlign: 'center' }}
        >
          ← Prev
        </button>
        <span style={{ flexShrink: 0, fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: R.metaText, textAlign: 'center', minWidth: 60 }}>
          {globalCurrentPage} / {globalTotalPages || articles.length}
        </span>
        <button
          data-testid="mobile-reader-next-btn"
          onClick={handleNext}
          disabled={isLast}
          style={{ flex: 1, padding: '10px 12px', borderRadius: 7, border: 'none', background: isLast ? R.railMuted : R.nextBtnBg, color: R.nextBtnText, fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 600, cursor: isLast ? 'default' : 'pointer', opacity: isLast ? 0.4 : 1, textAlign: 'center' }}
        >
          Next →
        </button>
      </div>
    </div>
    </>
  );
}


// ─── MAGAZINE READER V2 — public /magazine/:slug route ───────────────────────
// Same cockpit as OnlineReaderTestPage. Dynamic slug via useParams. No "Test Route" label.
export function MagazineReaderV2() {
  useReaderFonts();
  const { slug } = useParams();

  const [issue,       setIssue]       = useState(null);
  const [articles,    setArticles]    = useState([]);
  const [advertiserIndex, setAdvertiserIndex] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [segIdx,      setSegIdx]      = useState(0);
  const [fontSize,    setFontSize]    = useState(17);
  const [unlocked,    setUnlocked]    = useState(false);
  const rightRailRef = React.useRef(null);
  const leftRailScrollRef = React.useRef(null);

  // ── Global page sequence (pre-measurement pass) ───────────────────────────
  // pageCounts: { [articleId]: number } — measured page count per editorial article
  // sequenceMap: { [articleId]: { globalStartPage, pageCount } }
  // Re-measured whenever font size changes (debounced 300ms) or articles reload.
  const globalHiddenRef  = useRef(null);   // off-screen sandbox for all-article measurement
  const frameGeomRef     = useRef({ h: 0, w: 760 }); // last reported CenterCanvas frame size
  const [pageCounts,  setPageCounts]  = useState({});
  const [seqVersion,  setSeqVersion]  = useState(0);  // incremented to trigger re-measurement
  const fontDebounceRef = useRef(null);

  // Called by CenterCanvas ResizeObserver — refines frame dimensions when on an article
  const onBodyFrameSized = useCallback(({ h, w }) => {
    if (h > 0 && w > 0) {
      frameGeomRef.current = { h, w };
      // Bump to refine measurements when actual frame is available
      setSeqVersion(v => v + 1);
    }
  }, []);

  // Initial trigger: compute frame geometry from window dimensions when articles load.
  // This fires even when the reader is on the cover page (CenterCanvas early-returns
  // CoverPageCanvas, so its bodyFrameRef never fires onBodyFrameSized).
  // Bar heights: ExitBar(52) + CanvasTopBar(44) + ProgressBar(3) + BottomBar(54) = 153px
  // Rail widths: LeftRail(288) + RightRail(300) = 588px
  useEffect(() => {
    if (!articles.length) return;
    const h = Math.max(100, window.innerHeight - 153);
    const w = Math.max(100, window.innerWidth - 588);
    frameGeomRef.current = { h, w };
    setSeqVersion(v => v + 1);
  }, [articles]); // eslint-disable-line

  // Debounce font-size changes: rebuild sequence 300ms after the last click
  useEffect(() => {
    if (!articles.length) return;
    clearTimeout(fontDebounceRef.current);
    fontDebounceRef.current = setTimeout(() => setSeqVersion(v => v + 1), 300);
    return () => clearTimeout(fontDebounceRef.current);
  }, [fontSize]); // eslint-disable-line

  // Pre-measure all editorial articles when seqVersion bumps (initial load + font change)
  useEffect(() => {
    const { h, w } = frameGeomRef.current;
    const hiddenDiv = globalHiddenRef.current;
    if (!articles.length || h < 60 || !hiddenDiv) return;
    const editorial = articles.filter(a => !a._isAd && !a._isCover);
    const counts = {};
    for (const art of editorial) {
      counts[art.id] = measureArticlePageCount(art, h, w, fontSize, hiddenDiv);
    }
    setPageCounts(counts);
  }, [seqVersion, articles]); // eslint-disable-line

  // Derive global sequence: walk combined list assigning cumulative page positions
  const { sequenceMap, totalGlobalPages } = useMemo(() => {
    const map = {};
    let gPage = 1;
    for (const art of articles) {
      if (art._isCover) {
        map[art.id] = { globalStartPage: gPage, pageCount: 1 };
        gPage += 1;
      } else if (art._isAd) {
        map[art.id] = { globalStartPage: gPage, pageCount: 1 };
        gPage += 1;
      } else {
        const count = pageCounts[art.id] || 1;
        map[art.id] = { globalStartPage: gPage, pageCount: count };
        gPage += count;
      }
    }
    return { sequenceMap: map, totalGlobalPages: Math.max(1, gPage - 1) };
  }, [articles, pageCounts]);

  useEffect(() => {
    if (!slug) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      setSelectedIdx(0);
      setSegIdx(0);
      try {
        const issueRes = await axios.get(`${API}/magazine/${slug}`);
        const iss = issueRes.data;
        if (!active) return;
        setIssue(iss);

        const pagesRes = await axios.get(`${API}/magazine/${iss.id}/pages`);
        const EDITORIAL = [
          'editorial_article','in_focus','stay_smart','forward_brief',
          'opportunity_signal','executive_in_focus','greg_feature','gregs_crossroads',
          'elite_8','opportunity_pass','recurring_department',
        ];
        // Sponsor/ad pages: included in reader flow, do NOT require content_item_id
        const SPONSOR_TYPES = ['inside_front_sponsor', 'sponsor_full_page', 'inside_back_sponsor'];

        const editorialPages = pagesRes.data
          .filter(p => EDITORIAL.includes(p.page_type) && p.content_item_id);
        const sponsorPages = pagesRes.data
          .filter(p => SPONSOR_TYPES.includes(p.page_type));

        const contentItems = await Promise.all(
          editorialPages.map(p =>
            axios.get(`${API}/content/${p.content_item_id}`)
              .then(r => ({ ...r.data, pageNum: p.page_number }))
              .catch(() => null)
          )
        );
        if (!active) return;

        // Ad pages render directly from existing page fields — no content-item fetch needed
        const adItems = sponsorPages.map(p => ({
          id: p.id,
          pageNum: p.page_number,
          _isAd: true,
          title: p.sponsor_name || 'Advertisement',
          page_image_url: p.page_image_url,
          thumbnail_url: p.thumbnail_url,
          cta_url: p.cta_url,
          cta_label: p.cta_label,
          video_url: p.video_url,
          qr_destination_url: p.qr_destination_url,
          sponsor_name: p.sponsor_name,
          sponsor_disclosure: p.sponsor_disclosure,
          sponsorship_status: p.sponsorship_status,
          ad_layout: p.ad_layout || 'full_page',
          half_top_image_url: p.half_top_image_url,
          half_top_cta_url: p.half_top_cta_url,
          half_top_cta_label: p.half_top_cta_label,
          half_top_sponsor_name: p.half_top_sponsor_name,
          half_top_disclosure: p.half_top_disclosure,
          half_bottom_image_url: p.half_bottom_image_url,
          half_bottom_cta_url: p.half_bottom_cta_url,
          half_bottom_cta_label: p.half_bottom_cta_label,
          half_bottom_sponsor_name: p.half_bottom_sponsor_name,
          half_bottom_disclosure: p.half_bottom_disclosure,
        }));

        const combined = [...contentItems.filter(Boolean), ...adItems]
          .sort((a, b) => (a.pageNum || 0) - (b.pageNum || 0));

        // Prepend a virtual cover entry so the reader opens on the cover by default
        const coverEntry = iss.cover_image_url ? [{
          id: 'cover',
          _isCover: true,
          title: iss.title || iss.issue_date || 'Cover',
          cover_image_url: iss.cover_image_url,
          pageNum: -1,
        }] : [];

        setArticles([...coverEntry, ...combined]);

        // ── Advertiser Index: reliable detection of every ad/sponsor page ──────
        // Matches on page_type OR presence of any ad-related field, so a page is
        // never missed just because it uses an unexpected page_type in the future.
        const AD_PAGE_TYPES = ['inside_front_sponsor', 'sponsor_full_page', 'inside_back_sponsor'];
        // NOTE: page_image_url and the "full_page" ad_layout default are also present
        // on ordinary editorial pages (shared hero-image field / model default), so
        // they are NOT reliable ad-only signals on their own. Only page_type and the
        // half_top_/half_bottom_ fields are unique to sponsor/ad pages.
        const advertiserPages = pagesRes.data.filter(p =>
          AD_PAGE_TYPES.includes(p.page_type) ||
          p.ad_layout === 'two_half_page' ||
          !!p.half_top_image_url ||
          !!p.half_bottom_image_url
        );
        const rows = [];
        advertiserPages
          .sort((a, b) => (a.page_number || 0) - (b.page_number || 0))
          .forEach(p => {
            const articleIdx = coverEntry.length + combined.findIndex(a => a.id === p.id);
            const hasDistinctHalves = p.ad_layout === 'two_half_page'
              && p.half_top_sponsor_name && p.half_bottom_sponsor_name
              && p.half_top_sponsor_name !== p.half_bottom_sponsor_name;
            if (hasDistinctHalves) {
              rows.push({ key: `${p.id}-top`, pageNum: p.page_number, sponsorLabel: p.half_top_sponsor_name, articleIdx, thumbnail_url: p.half_top_thumbnail_url || p.thumbnail_url });
              rows.push({ key: `${p.id}-bottom`, pageNum: p.page_number, sponsorLabel: p.half_bottom_sponsor_name, articleIdx, thumbnail_url: p.half_bottom_thumbnail_url || p.thumbnail_url });
            } else {
              rows.push({ key: p.id, pageNum: p.page_number, sponsorLabel: p.sponsor_name || 'Sponsor placement reserved', articleIdx, thumbnail_url: p.thumbnail_url });
            }
          });
        setAdvertiserIndex(rows);
      } catch (e) {
        console.error('MagazineReaderV2 load error:', e);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [slug]);

  const handleSelect = useCallback((idx) => { setSelectedIdx(idx); setSegIdx(0); }, []);
  // Gate applies to editorial articles only, and never to the first editorial article
  // (free preview) — ad pages inserted before it must not shift which article is "free".
  const firstEditorialIdx = articles.findIndex(a => !a._isAd && !a._isCover);
  // OTP access control deferred — all pages open in Web Edition
  const isGated = false; // eslint-disable-line no-unused-vars

  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: R.outer }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 36, height: 36, border: `3px solid ${R.railBorder}`, borderTop: `3px solid ${R.tealDeep}`, borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 0.9s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: R.railSub, letterSpacing: 0.5 }}>Loading reader…</div>
        </div>
      </div>
    );
  }

  // ── Empty state: issue loaded but no articles yet (draft shells) ─────────────
  if (!articles.length) {
    const comingTitle = issue?.title ? `${issue.title} is being prepared.` : 'This issue is being prepared.';
    return (
      <div data-testid="magazine-reader-v2-root" style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: R.outer, overflow: 'hidden' }}>
        <div data-testid="reader-exit-bar" style={{ flex: '0 0 auto', background: R.rail, borderBottom: `1px solid ${R.railBorder}`, padding: '9px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 10 }}>
          <Link to="/" data-testid="reader-back-link" style={{ fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: 600, color: R.railAccent, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
            ← Back to Main Website
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontFamily: 'Newsreader, Georgia, serif', fontSize: 14, fontWeight: 500, color: R.railText }}>Opportunity Post</span>
            <span style={{ width: 1, height: 14, background: R.railDivider, flexShrink: 0 }} />
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: 1.4, color: R.tealBadgeText, background: R.tealBadge, padding: '2px 8px', borderRadius: 3, textTransform: 'uppercase' }}>Web Edition</span>
            {issue?.issue_date && (
              <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, letterSpacing: 0.8, color: R.tealBadgeText, background: R.tealBadge, padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase' }}>
                {issue.issue_date}
              </span>
            )}
          </div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, letterSpacing: 1.2, color: R.railSub, textTransform: 'uppercase' }}>
            {issue?.issue_date ? `${issue.issue_date} · VOL. ${issue.volume_number || 6} NO. ${String(issue.issue_number || '').padStart(2, '0')}` : 'Opportunity Post Monthly'}
          </div>
          <div style={{ fontFamily: 'Newsreader, Georgia, serif', fontSize: 22, fontWeight: 700, color: R.railText, textAlign: 'center', maxWidth: 420, lineHeight: 1.35 }}>
            {comingTitle}
          </div>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: R.railSub }}>
            Check back soon.
          </div>
        </div>
      </div>
    );
  }

  // ── Mobile layout (< 768px) ──────────────────────────────────────────────────
  if (!loading && articles.length && isMobile) {
    return (
      <MobileReaderLayout
        issue={issue}
        articles={articles}
        selectedIdx={selectedIdx}
        handleSelect={handleSelect}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
      />
    );
  }

  return (
    <div
      data-testid="magazine-reader-v2-root"
      style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: R.outer, overflow: 'hidden' }}
    >
      {/* Global measurement sandbox — must remain in DOM, never visible */}
      <div
        ref={globalHiddenRef}
        aria-hidden="true"
        style={{ position: 'fixed', top: '-9999px', left: 0, width: 716, visibility: 'hidden', pointerEvents: 'none', zIndex: -1 }}
      />

      {/* Exit bar — Web Edition header */}
      <div
        data-testid="reader-exit-bar"
        style={{
          flex: '0 0 auto', background: '#FFFDF8', borderBottom: '1px solid #E5E1D8',
          padding: '10px 24px', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center',
        }}
      >
        <Link
          to="/"
          data-testid="reader-back-link"
          style={{ fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 600, color: '#2F9486', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5 }}
        >
          ← Back to Main Website
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontFamily: 'Newsreader, Georgia, serif', fontSize: 16, fontWeight: 500, color: '#081D30', letterSpacing: 0.1 }}>
            Opportunity Post
          </span>
          <span style={{ width: 1, height: 16, background: '#C49630', flexShrink: 0 }} />
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: '#2F9486', textTransform: 'uppercase' }}>
            Web Edition
          </span>
        </div>
        <div style={{ justifySelf: 'flex-end' }}>
          {issue?.issue_date && (
            <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: 11, color: '#94A3B0' }}>{issue.issue_date}</span>
          )}
        </div>
      </div>

      {/* 3-column cockpit */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <LeftRail
          articles={articles}
          selectedIdx={selectedIdx}
          onSelect={handleSelect}
          issue={issue}
          scrollRef={leftRailScrollRef}
          sequenceMap={sequenceMap}
        />

        <CenterCanvas
          article={articles[selectedIdx]}
          segIdx={segIdx}
          setSegIdx={setSegIdx}
          fontSize={fontSize}
          setFontSize={setFontSize}
          isGated={isGated}
          selectedIdx={selectedIdx}
          articlesCount={articles.length}
          onPrevArticle={() => handleSelect(selectedIdx - 1)}
          onNextArticle={() => handleSelect(selectedIdx + 1)}
          rightRailRef={rightRailRef}
          onContents={() => leftRailScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          onBodyFrameSized={onBodyFrameSized}
          sequenceMap={sequenceMap}
          totalGlobalPages={totalGlobalPages}
        />

        <RightRailV2
          article={articles[selectedIdx]}
          issue={issue}
          unlocked={unlocked}
          onUnlock={() => setUnlocked(true)}
          innerRef={rightRailRef}
          advertiserIndex={advertiserIndex}
          onSelectAdvertiser={handleSelect}
          articles={articles}
          sequenceMap={sequenceMap}
        />
      </div>
    </div>
  );
}

// ─── Advertisers Index Accordion ──────────────────────────────────────────────
const ADVERTISER_INITIAL_SHOW = 6;

function AdvertisersIndexAccordion({ advertiserIndex, currentPageNum, onSelectAdvertiser, sequenceMap }) {
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [sortMode, setSortMode] = useState(0); // 0=page order, 1=A-Z ↑, 2=Z-A ↓
  if (!advertiserIndex.length) return null;

  const placementCount = advertiserIndex.length;
  const tileColors = ['#C49630', '#2F9486', '#5B3A8C', '#C0392B', '#1A6B9E', '#3D6B4F', '#8C4A2F', '#2C5F8C'];
  const getTileColor = (name = '') => name ? tileColors[name.charCodeAt(0) % tileColors.length] : tileColors[0];
  // Extract the page ID from a row key (strips -top / -bottom suffix for two-half ads)
  const getAdPageId = (key = '') => key.replace(/-(?:top|bottom)$/, '');

  // Derive sorted list: page order (prop default) | A-Z ascending | Z-A descending
  const sortedRows = sortMode === 0
    ? advertiserIndex
    : [...advertiserIndex].sort((a, b) => {
        const na = a.sponsorLabel || '';
        const nb = b.sponsorLabel || '';
        return sortMode === 1 ? na.localeCompare(nb) : nb.localeCompare(na);
      });
  const visibleRows = expanded ? sortedRows : sortedRows.slice(0, ADVERTISER_INITIAL_SHOW);
  const hiddenCount = sortedRows.length - ADVERTISER_INITIAL_SHOW;

  return (
    <div data-testid="reader-advertiser-index" style={{ borderBottom: '1px solid #EDE9DF' }}>
      {/* Header row — open/close on left, A-Z sort + chevron on right */}
      <div style={{ width: '100%', padding: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button
          data-testid="reader-advertiser-index-toggle"
          onClick={() => setOpen(o => !o)}
          style={{
            flex: 1, border: 'none', background: 'transparent',
            cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center',
            gap: 8, padding: 0, minWidth: 0,
          }}
        >
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: 9, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: '#94A3B0' }}>
            Advertisers Index
          </span>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: 9, color: '#2F9486', background: 'rgba(47,148,134,0.10)', padding: '2px 7px', borderRadius: 4, fontWeight: 600 }}>
            {placementCount}
          </span>
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          <button
            data-testid="reader-advertiser-sort-toggle"
            onClick={() => setSortMode(m => (m + 1) % 3)}
            title="Toggle sort order"
            style={{
              border: `1px solid ${sortMode > 0 ? 'rgba(47,148,134,0.4)' : '#DDD9D2'}`,
              borderRadius: 3,
              background: sortMode > 0 ? 'rgba(47,148,134,0.10)' : 'transparent',
              color: sortMode > 0 ? '#2F9486' : '#B0A898',
              fontFamily: 'Archivo, sans-serif', fontSize: 8, fontWeight: sortMode > 0 ? 700 : 500,
              padding: '2px 4px', cursor: 'pointer', lineHeight: 1,
              letterSpacing: 0.3, whiteSpace: 'nowrap',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {sortMode === 2 ? 'A-Z ↓' : 'A-Z ↑'}
          </button>
          <span
            data-testid="reader-advertiser-index-chevron"
            onClick={() => setOpen(o => !o)}
            style={{
              display: 'inline-block', fontSize: 11, color: '#94A3B0',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease', cursor: 'pointer',
            }}
          >
            ▾
          </span>
        </div>
      </div>

      {/* Expanded panel — flat list, no internal scroll */}
      <div style={{
        maxHeight: open ? 2000 : 0,
        opacity: open ? 1 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.28s ease, opacity 0.2s ease',
      }}>
        <div style={{ padding: '0 14px 4px' }}>
          {visibleRows.map(row => {
            const adPageId = getAdPageId(row.key);
            const globalPage = sequenceMap?.[adPageId]?.globalStartPage ?? row.pageNum;
            const isActive = row.pageNum === currentPageNum;
            const name = row.sponsorLabel || 'Advertisement';
            const tileColor = getTileColor(name);
            return (
              <button
                key={row.key}
                data-testid={`reader-advertiser-index-item-${row.key}`}
                onClick={() => row.articleIdx >= 0 && onSelectAdvertiser?.(row.articleIdx)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                  padding: '8px 0', border: 'none', borderBottom: '1px solid #EDE9DF',
                  background: isActive ? '#F7F3EC' : 'transparent',
                  borderLeft: `3px solid ${isActive ? '#081D30' : 'transparent'}`,
                  cursor: 'pointer', transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#FAFAF8'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                {/* Badge / Logo */}
                {row.thumbnail_url
                  ? <img src={row.thumbnail_url} alt={name} style={{ width: 32, height: 32, borderRadius: 5, objectFit: 'contain', flexShrink: 0, background: '#F7F4EE', border: '1px solid #EDE9DF' }} />
                  : <div style={{ width: 32, height: 32, borderRadius: 5, background: tileColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: 13, fontWeight: 800, color: '#fff' }}>
                        {name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                }
                {/* Name */}
                <span style={{
                  fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: isActive ? 700 : 500,
                  color: '#081D30', flex: 1, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {name}
                </span>
                {/* Global page number */}
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: 11, fontWeight: isActive ? 700 : 400, color: isActive ? '#081D30' : '#94A3B0', flexShrink: 0, marginLeft: 4 }}>
                  p.{globalPage}
                </span>
              </button>
            );
          })}

          {/* Expand All / Collapse toggle */}
          {hiddenCount > 0 && !expanded && (
            <button
              data-testid="reader-advertiser-index-expand"
              onClick={() => setExpanded(true)}
              style={{
                width: '100%', padding: '8px 0', border: 'none', background: 'transparent',
                fontFamily: 'Archivo, sans-serif', fontSize: 11, color: '#2F9486',
                cursor: 'pointer', textAlign: 'center', letterSpacing: 0.2,
              }}
            >
              Expand All ({hiddenCount} more)
            </button>
          )}
          {expanded && sortedRows.length > ADVERTISER_INITIAL_SHOW && (
            <button
              data-testid="reader-advertiser-index-collapse"
              onClick={() => setExpanded(false)}
              style={{
                width: '100%', padding: '8px 0', border: 'none', background: 'transparent',
                fontFamily: 'Archivo, sans-serif', fontSize: 11, color: '#94A3B0',
                cursor: 'pointer', textAlign: 'center', letterSpacing: 0.2,
              }}
            >
              Collapse ▲
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Right Rail V2 — dynamic "View Full Issue" link ─────
function RightRailV2({ article, issue, unlocked, onUnlock, innerRef, advertiserIndex = [], onSelectAdvertiser, articles = [], sequenceMap }) {
  const recommended = articles.filter(a => !a._isAd && !a._isCover && a.id !== article?.id).slice(0, 4);

  return (
    <aside
      data-testid="reader-right-rail"
      ref={innerRef}
      style={{ width: 288, minWidth: 288, background: '#FFFFFF', borderLeft: '1px solid #E5E1D8', display: 'flex', flexDirection: 'column', overflowY: 'auto', flexShrink: 0 }}
    >
      {/* 1. Advertisers Index — moved to top */}
      <AdvertisersIndexAccordion
        advertiserIndex={advertiserIndex}
        currentPageNum={article?.pageNum}
        onSelectAdvertiser={onSelectAdvertiser}
        sequenceMap={sequenceMap}
      />

      {/* 2. Web Edition Access */}
      <div style={{ padding: '14px', borderBottom: '1px solid #EDE9DF' }}>
        <div style={{ border: '1px solid #EDE9DF', borderRadius: 10, padding: '14px', background: '#F7F4EE' }}>
          <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 9, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: '#94A3B0', marginBottom: 8 }}>
            Get Every Edition
          </div>
          <div style={{ fontFamily: 'Newsreader, Georgia, serif', fontSize: 14, fontWeight: 500, color: '#081D30', marginBottom: 6, lineHeight: 1.3 }}>
            Free Web Edition
          </div>
          <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 10.5, color: '#6B7F8C', lineHeight: 1.6, marginBottom: 12 }}>
            Get newsletter delivery, early access alerts, and member content with a free membership.
          </div>
          <Link
            to="/magazine#subscribe"
            data-testid="reader-free-membership-btn"
            style={{ display: 'block', padding: '9px 12px', borderRadius: 6, background: '#C49630', color: '#fff', fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 700, textDecoration: 'none', textAlign: 'center' }}
          >
            Sign Up Free →
          </Link>
        </div>
      </div>

      {/* 3. Recommended Reading */}
      {recommended.length > 0 && (
        <div style={{ padding: '14px 14px 0', borderBottom: '1px solid #EDE9DF' }}>
          <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 9, fontWeight: 700, letterSpacing: 1.4, textTransform: 'uppercase', color: '#94A3B0', marginBottom: 10 }}>
            Recommended Reading
          </div>
          {recommended.map((rec, i) => {
            const thumb = rec.hero_image_url || rec.featured_image_url;
            return (
              <button
                key={rec.id}
                data-testid={`reader-rec-item-${i}`}
                onClick={() => onSelectAdvertiser?.(articles.findIndex(a => a.id === rec.id))}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', textAlign: 'left',
                  padding: '9px 0', border: 'none', cursor: 'pointer', background: 'transparent',
                  borderBottom: i < recommended.length - 1 ? '1px solid #EDE9DF' : 'none',
                }}
              >
                <div style={{ width: 46, height: 46, borderRadius: 6, flexShrink: 0, overflow: 'hidden', background: '#EDE9DF' }}>
                  {thumb
                    ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #EDE9DF 0%, #D8D3C8 100%)' }} />
                  }
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 11.5, fontWeight: 600, color: '#1A2E3B', lineHeight: 1.38, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {rec.title}
                  </div>
                  <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 9.5, color: '#94A3B0', marginTop: 2 }}>
                    {readTime(rec.body)}
                  </div>
                </div>
              </button>
            );
          })}
          <div style={{ height: 14 }} />
        </div>
      )}

      {/* Back to magazine */}
      <div style={{ marginTop: 'auto', padding: '14px', borderTop: '1px solid #EDE9DF' }}>
        <Link
          to="/magazine"
          data-testid="reader-full-issue-link"
          style={{ display: 'block', padding: '9px 12px', borderRadius: 6, border: '1px solid #E5E1D8', background: '#fff', color: '#1A2E3B', fontFamily: 'Archivo, sans-serif', fontSize: 12, fontWeight: 600, textDecoration: 'none', textAlign: 'center' }}
        >
          ← Archives · Members Only
        </Link>
      </div>
    </aside>
  );
}


// ─── Main Page ────────────────────────────────────────────────────────────────
export default function OnlineReaderTestPage() {
  useReaderFonts();

  const [issue, setIssue] = useState(null);
  const [issueSlug, setIssueSlug] = useState(ISSUE_SLUG);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [segIdx, setSegIdx] = useState(0);
  const [fontSize, setFontSize] = useState(17);
  const [unlocked, setUnlocked] = useState(false);
  const [accessState, setAccessState] = useState(null); // null while loading
  const rightRailRef = React.useRef(null);
  const pingTimerRef = useRef(null);

  // Load issue + editorial pages + check access
  useEffect(() => {
    const load = async () => {
      try {
        // Ensure visitor cookie is set before any access check
        await axios.get(`${API}/reader/visitor`);

        // Resolve which issue to load — use ISSUE_SLUG if set, else fetch latest
        let slug = ISSUE_SLUG;
        if (!slug) {
          const latestRes = await axios.get(`${API}/magazine/latest`);
          slug = latestRes.data.slug;
        }
        setIssueSlug(slug);

        const issueRes = await axios.get(`${API}/magazine/${slug}`);
        const iss = issueRes.data;
        setIssue(iss);

        const pagesRes = await axios.get(`${API}/magazine/${iss.id}/pages`);
        const editorialPages = pagesRes.data
          .filter(p => p.page_type === 'editorial_article' && p.content_item_id)
          .sort((a, b) => (a.page_number || 0) - (b.page_number || 0));

        const contentItems = await Promise.all(
          editorialPages.map(p =>
            axios.get(`${API}/content/${p.content_item_id}`)
              .then(r => ({ ...r.data, pageNum: p.page_number }))
              .catch(() => null)
          )
        );
        setArticles(contentItems.filter(Boolean));

        // Check access after visitor cookie is in place
        const accessRes = await axios.get(`${API}/magazine/${slug}/access`);
        setAccessState(accessRes.data);
        if (accessRes.data.allowed) setUnlocked(true);

      } catch (e) {
        console.error('Reader load error:', e);
        // On error, allow reading to avoid blocking users on access check failures
        setAccessState({ allowed: true, reason: 'preview', access_tier: 'public_current', preview_info: null });
        setUnlocked(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Preview heartbeat: ping backend every 30s when in active preview
  useEffect(() => {
    const isPreview = accessState?.reason === 'preview' && accessState?.allowed;
    if (!isPreview) {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
      return;
    }
    pingTimerRef.current = setInterval(async () => {
      try {
        const res = await axios.post(`${API}/magazine/${issueSlug}/preview-ping`, { seconds: 30 });
        setAccessState(res.data);
        if (!res.data.allowed) {
          setUnlocked(false);
          clearInterval(pingTimerRef.current);
          pingTimerRef.current = null;
        }
      } catch (_) {
        // Silently ignore network errors during ping
      }
    }, 30000);
    return () => {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    };
  }, [accessState?.reason, accessState?.allowed, issueSlug]); // eslint-disable-line

  const handleSelect = useCallback((idx) => {
    setSelectedIdx(idx);
    setSegIdx(0);
  }, []);

  // isGated: access check completed AND access is denied AND not already unlocked
  const isGated = accessState !== null && !unlocked;

  const scrollToRightRail = () => {
    if (rightRailRef?.current) rightRailRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: R.outer }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 36, height: 36, border: `3px solid ${R.railBorder}`, borderTop: `3px solid ${R.tealDeep}`,
            borderRadius: '50%', margin: '0 auto 12px',
            animation: 'spin 0.9s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: R.railSub, letterSpacing: 0.5 }}>
            Loading reader…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="online-reader-root"
      style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: R.outer, overflow: 'hidden' }}
    >
      <ExitBar issue={issue} />

      {/* 3-column cockpit */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <LeftRail
          articles={articles}
          selectedIdx={selectedIdx}
          onSelect={handleSelect}
          issue={issue}
        />

        {/* Center column: canvas + gate overlay */}
        <div style={{ flex: 1, position: 'relative', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <CenterCanvas
            article={articles[selectedIdx]}
            segIdx={segIdx}
            setSegIdx={setSegIdx}
            fontSize={fontSize}
            setFontSize={setFontSize}
            isGated={isGated}
            selectedIdx={selectedIdx}
            articlesCount={articles.length}
            onPrevArticle={() => handleSelect(selectedIdx - 1)}
            onNextArticle={() => handleSelect(selectedIdx + 1)}
            rightRailRef={rightRailRef}
          />
          {isGated && (
            <GateOverlay
              onScrollRight={scrollToRightRail}
              reason={accessState?.reason}
            />
          )}
        </div>

        <RightRail
          article={articles[selectedIdx]}
          issue={issue}
          unlocked={unlocked}
          onUnlock={() => setUnlocked(true)}
          gateReason={accessState?.reason}
          innerRef={rightRailRef}
        />
      </div>
    </div>
  );
}
